/**
 * Exhibition guided tour: a scripted walkthrough of the application.
 *
 * The visitor arrives from a QR code knowing nothing about T-Soles, so the tour
 * introduces the product and its interface first and only then offers to teach
 * the design process. It is not a tour of the loaded design.
 *
 * Every sentence is written here and spoken verbatim through the existing
 * SPEAK_EXACTLY channel, rather than described to the agent and improvised.
 * A scripted tour is the same for every visitor, stays inside what the product
 * actually does, and lets each icon highlight be timed to the sentence naming
 * it — none of which survives asking the model to present the app from memory.
 *
 * Advancing is driven by the audio, not by a timer: a step ends when the
 * assistant's voice has actually fallen quiet, so a slow network stretches the
 * tour instead of desynchronising it.
 */

import {
  currentVoiceLang,
  lockVoiceLang,
  unlockVoiceLang,
  voiceText,
  VoiceLang,
} from "Voice/voiceLanguage";
import {
  estimateSpeechMs,
  isAgentAudible,
  isAgentSpeaking,
  noteSpokenLine,
  resetAgentAudioTimeline,
  setAgentOutputVolume,
  setAgentSpeaking,
  waitMs,
  waitUntilAgentAudible,
  waitUntilAgentQuiet,
  waitUntilAgentSpeaking,
  voiceLog,
} from "Voice/voiceSpeechSync";
import {
  focusVoiceAddition,
  orbitVoiceCamera,
  setModelCategory,
  stopVoiceOrbit,
  holdVoiceCamera,
  releaseVoiceFocus,
  fitCameraToVisible,
  dispatchViewerMode,
  dispatchProjectSetting,
  moveVoiceAddition,
  openPropertyPanel,
  exitEngravingIfNeeded,
  VOICE_OPEN_PANEL_EVENT,
  VOICE_CLOSE_PANELS_EVENT,
  VOICE_SET_THEME_EVENT,
} from "Voice/voiceCommands";
import { useDesignStore } from "context/DesignContext";
import { useViewerStore } from "context/ViewerContext";
import { requestExhibitionTour, skipExhibitionTour } from "Exhibition/exhibitionEvents";
import {
  TOUR_BARS,
  TOUR_TARGETS,
  clearHighlights,
  clickTarget,
  hasTarget,
  highlightGroup,
  highlightTargets,
  isGroupVisible,
  isMobileLayout,
  isTargetVisible,
  closeMobileOverlays,
  dismissTopPanel,
  isPanelOpen,
  isTargetActionable,
  isTargetSelected,
  TOUR_PANEL_TARGETS,
  TOUR_TARGETS_MOBILE,
} from "./tourSpotlight";

export type TourControlAction =
  | "answerQuestion"
  | "introduceApp"
  | "introduceProcess"
  | "acceptLesson"
  | "declineLesson"
  | "next"
  | "repeat"
  // "stop talking": the script breaks off and the visitor is asked whether to
  // carry on. Distinct from dismiss, which is the visitor saying they are done.
  | "skip"
  // Was the spoken equivalent of the Skip tour button. Withdrawn: both
  // narrations run with the microphone shut, so nothing the visitor says can
  // reach a tool mid-script, and stopping is the on-screen control's job alone.
  // Kept in the union only so a stale published agent can be refused by name.
  | "dismiss"
  | "restart";

/**
 * What a step opens once its line has been spoken.
 *
 * Naming an icon and moving on left the user to imagine what pressing it does.
 * A step that leads somewhere now goes there: the panel opens, stays up long
 * enough to be taken in, and is put away again before the next icon is named.
 *
 * Deliberately only for the icons that open something to look at. Buttons that
 * act — New Design, Print, Align — are described and left alone: pressing those
 * on the user's own design is not a demonstration, it is a change they did not
 * ask for.
 */
type TourReveal = {
  /** The button to press. */
  open: string;
  /**
   * How it is put away again.
   *
   * "panel" is a modal or the property panel, closed through the same events
   * the voice assistant uses, with the backdrop as the fallback the mobile
   * drawer's own panels need. "toggle" is a dropdown or an expanding row, which
   * closes by pressing the thing that opened it a second time.
   */
  close: "panel" | "toggle";
  /** How long it stays on screen. A couple of seconds, unless stated. */
  holdMs?: number;
  /**
   * Tabs inside the opened panel to step through, each held in turn.
   *
   * A panel with two tabs is only half shown by opening it. Listing both is
   * deliberate rather than listing "the other one": which tab a panel opens on
   * depends on the design and on where it was left last time, so the reveal
   * works out what is already on screen instead of assuming.
   */
  tabs?: string[];
  /**
   * Demonstrate a view control, rather than opening anything. Holds on each
   * state it has not already shown, then puts the view back as it was.
   *
   * The icon itself is pressed once, so the control behaves as it does under a
   * finger and the mobile eye menu closes the way it should. Everything after
   * that — the further states of wireframe and feet view, and the restore at
   * the end — is written to the state directly, because that menu closed on the
   * first press and pressing the icon again would mean reopening it and shutting
   * it a second time, which is the flicker. The one reopen left is the next
   * step's own, which it needs anyway to light its icon.
   *
   * Writing the state is equivalent to pressing: every one of these store
   * actions is a plain `set` on this one field and nothing else.
   */
  view?: ViewKey;
  /**
   * Further viewer state to switch on at the same moment, and put back with it.
   *
   * For the colour step: showing the pressure is worth more with the recording
   * running on it, which is the Animate box in Project Settings. The two belong
   * to one demonstration, so they go on together and come off together.
   */
  alongside?: Record<string, unknown>;
  /**
   * Demonstrate the light/dark switch.
   *
   * Its own mode because the theme is not viewer state: it lives in
   * ThemeContext and is written straight to localStorage. That last part is why
   * it is put back by name rather than by a second press — a run cut mid-hold
   * would otherwise leave the whole application in the other theme, and leave
   * it that way for every session after.
   */
  theme?: boolean;
  /**
   * The overlay to put away while this demonstration is on screen, and bring
   * back after it.
   *
   * For the theme row on mobile, which sits in the drawer and — unlike every
   * item in the eye menu — does not dismiss it on click. The theme is the whole
   * application changing, and the drawer covers most of it, so the drawer is
   * closed for the hold the way the other controls close their own menu.
   */
  overlay?: string;
  /**
   * Extra gate for a control that has nothing to show.
   *
   * `isTargetActionable` already stops a reveal on a disabled button, which
   * covers the desktop colour button. Its mobile twin is a plain menu item with
   * no disabled state, so without this the walkthrough would hold on a screen
   * that did not change.
   */
  enabled?: () => boolean;
};

/** The view-state fields the left bar and the mobile eye drive. */
type ViewKey =
  | "liveMode"
  | "coloring"
  | "perspectiveCamera"
  | "clipping"
  | "grids"
  | "wireframe"
  | "feetView"
  | "measurementSystem";

/**
 * The order the multi-state controls cycle in, matching cycleWireframe and
 * cycleFeetView. Anything not listed here is a plain on/off control, which is
 * shown by flipping it and flipping it back.
 */
const VIEW_CYCLE: Partial<Record<ViewKey, readonly string[]>> = {
  wireframe: ["insole", "wireframe", "both"],
  feetView: ["both", "right", "left"],
  // A two-way switch rather than a boolean, so it is listed rather than flipped.
  measurementSystem: ["metric", "imperial"],
};

type TourStep = {
  id: string;
  /** Runs before the line is spoken: highlight icons, open a dialog, change the view. */
  setup?: (ctx: TourContext) => Promise<void> | void;
  /**
   * Built at run time so it picks up the session language.
   *
   * May be asynchronous, because some steps produce their line by calling a
   * design tool — setInsoleParam returns the sentences that go with the camera
   * walk it starts. Those are resolved only once the assistant has fallen
   * silent, so the camera does not set off before there is anything to say.
   */
  line: () => string | Promise<string>;
  /**
   * Light the icon this step describes.
   *
   * Kept apart from `setup` because of when it runs. `setup` happens before the
   * line is even sent, and sending it costs a round trip to the agent plus
   * generation and speech synthesis — a second or more during which the icon
   * would already be lit and nothing said about it. This runs on the first
   * audible frame instead, so icon and sentence arrive together.
   */
  spotlight?: (ctx: TourContext) => void;
  /**
   * After this line, prefer live silence over a leftover audio timeline so the
   * next step does not sit in dead air (used for long lines like sandal+print).
   */
  tightenHandoff?: boolean;
  /**
   * Runs once the line has finished being spoken. This is what makes a reveal
   * land on the words that promise it: a step says "you can see it like this"
   * and the thing appears as the sentence ends, rather than before it starts.
   */
  after?: (ctx: TourContext) => Promise<void> | void;
  /**
   * Open what this step just described, hold it, and close it again.
   *
   * Runs between `line` and `after`, so the panel appears on the words that
   * promised it and the step's own clean-up still has the last say.
   */
  reveal?: TourReveal;
  /** Hold here until tourControl answers, instead of advancing on its own. */
  awaitsAnswer?: boolean;
  /** How long to wait for that answer before falling back. */
  answerTimeoutMs?: number;
  /** What silence means for this question. */
  defaultAnswer?: TourControlAction;
  /**
   * Skip this step when the named target is not on screen. Which buttons are
   * rendered depends on the visitor's role, and below 1250px the whole desktop
   * toolbar is hidden, so the tour must not narrate an icon that is not there
   * for them to look at.
   */
  skipWhenMissing?: string;
  /** Skip when this bar is not on screen, for the steps that introduce a bar. */
  skipWhenBarHidden?: string;
};

export type TourContext = {
  api: any;
  /** Speak a line verbatim. Returns false when the session is gone. */
  speak: (text: string) => boolean;
  isConnected: () => boolean;
  /**
   * Nudge the agent to stop holding a leftover turn so the next SPEAK_EXACTLY
   * is accepted (needed after long lines like pressure → params-intro).
   */
  nudgeAgent?: () => void;
  /**
   * False when this session asked the agent to skip its opening greeting, so
   * the tour need not sit through the full greeting wait before its first line.
   */
  expectsGreeting?: boolean;
  /**
   * True while this session's opening message is still to be heard: one was
   * asked for and the assistant has not made a sound yet. Read at the moment
   * the tour starts, which may be long after the session opened.
   */
  greetingPending?: () => boolean;
  /**
   * Mute or unmute the visitor's microphone.
   */
  setMicMuted?: (muted: boolean) => void;
  /**
   * Exhibition demonstration: keep the mic deaf for the whole scripted run so
   * booth noise cannot interrupt SPEAK_EXACTLY or answer mid-tour. Opened again
   * only when the tour finishes cleanly.
   */
  deafMic?: boolean;
  /**
   * Leave the session's language pin alone.
   *
   * The walkthrough normally follows whatever language the user speaks, so it
   * releases the lock on the way in. An exhibition session must not: it is pinned
   * to English on purpose, because with the microphone open at a stand the room —
   * and the assistant's own voice through the speakers — was enough to make the
   * agent switch language and then read scripted lines in it. The walkthrough is
   * reachable from an exhibition session now, so it has to respect that pin.
   */
  keepLanguage?: boolean;
  /** Called once the tour has finished or been skipped. */
  onFinished?: () => void;
  /**
   * Called when the visitor asked to stop and then declined the other half.
   * Treated exactly like pressing Skip: the microphone closes and the controls
   * stay away.
   */
  onDismissed?: () => void;
};

// ───────────────────────────────────────────────────────────── script

const L = (en: string, nl: string, fa: string, zh: string) => () => voiceText({ en, nl, fa, zh });

/**
 * English-only line, for the exhibition demonstration.
 *
 * The stand is run in English, so the demo script is not translated. The
 * Tutorial, which any user can run in their own language, still goes through L
 * and the four-language layer.
 */
const E = (en: string) => () => en;

/**
 * Project Settings, said the same way on both layouts.
 *
 * Shared rather than written out twice so the two cannot drift apart again: the
 * panel is the same panel, and the desktop and mobile steps had grown different
 * descriptions of it.
 */
const PROJECT_SETTINGS_LINE = L(
  "Project Settings. This controls what you see in the scene: the insole, the bones, the shoe, their colours and transparency. It can also play the pressure analysis as an animation.",
  "Projectinstellingen. Dit bepaalt wat je in beeld ziet: de zool, de botten, de schoen, hun kleuren en transparantie. Het kan de druk ook als animatie afspelen.",
  "تنظیمات پروژه. کنترل می‌کند چه چیزی در صحنه دیده شود: کفی، استخوان‌ها، کفش، رنگ‌ها و شفافیت آن‌ها. فشار را هم می‌تواند به‌صورت انیمیشن پخش کند.",
  "项目设置。控制场景中显示的内容：鞋垫、骨骼、鞋子及其颜色与透明度。它还能把压力以动画形式回放。"
);


/** Phase 1 — the application introduction, chosen at the opening question. */
/**
 * Phase 2, mobile and tablet. The same three groups as the desktop bars, but
 * reached through the three buttons this layout actually has: the menu at the
 * top left for what the top bar carries, and the pencil and eye at the top
 * right for the right and left bars. Each menu is opened before its items are
 * pointed at, because they do not exist on screen while it is closed.
 */
const BAR_STEPS_MOBILE: TourStep[] = [
  {
    id: "m-menu-intro",
    skipWhenMissing: TOUR_TARGETS_MOBILE.menu,
    setup: async () => {
      // The drawer and the two menus stack rather than replace one another, so
      // the previous section is dismissed before this one opens anything.
      closeMobileOverlays();
      await waitMs(240);
    },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.menu),
    line: L(
      "This button at the top left opens the main menu. Everything a design needs to start and to finish is in there. Let me open it.",
      "Deze knop linksboven opent het hoofdmenu. Alles wat een ontwerp nodig heeft om te beginnen en af te ronden zit daarin. Ik open hem even.",
      "این دکمه بالا سمت چپ منوی اصلی را باز می‌کند. هر چه یک طرح برای شروع و پایان لازم دارد آنجاست. بازش می‌کنم.",
      "左上角这个按钮会打开主菜单。设计从开始到完成所需的一切都在里面。我来打开它。"
    ),
    after: async () => {
      clickTarget(TOUR_TARGETS_MOBILE.menu);
      await waitMs(200);
    },
  },
  {
    id: "m-new-design",
    skipWhenMissing: TOUR_TARGETS_MOBILE.newDesign,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.newDesign),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.newDesign),
    line: L(
      "New Design. This starts a fresh insole from scratch.",
      "Nieuw ontwerp. Dit begint een nieuwe zool vanaf nul.",
      "طرح جدید. یک کفی تازه از صفر شروع می‌کند.",
      "新建设计。从零开始创建一只新鞋垫。"
    ),
  },
  {
    id: "m-database",
    skipWhenMissing: TOUR_TARGETS_MOBILE.database,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.database),
    reveal: { open: TOUR_TARGETS_MOBILE.database, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.database),
    line: L(
      "Database. Every design you save lives here, and this is where you search them and open one again.",
      "Database. Elk ontwerp dat je opslaat staat hier, en hier zoek je ze op en open je er weer een.",
      "دیتابیس. هر طرحی که ذخیره می‌کنید اینجاست و از همین‌جا جستجو و دوباره باز می‌شود.",
      "数据库。您保存的每个设计都在这里，也在这里搜索并重新打开。"
    ),
  },
  {
    id: "m-template",
    skipWhenMissing: TOUR_TARGETS_MOBILE.template,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.template),
    reveal: { open: TOUR_TARGETS_MOBILE.template, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.template),
    line: L(
      "Template. A design you have already shaped and saved, so you can start from it instead of from scratch.",
      "Sjabloon. Een ontwerp dat je al gevormd en opgeslagen hebt, zodat je daarvandaan begint in plaats van bij nul.",
      "قالب. طرحی که قبلاً شکل داده و ذخیره کرده‌اید تا به‌جای شروع از صفر از آن شروع کنید.",
      "模板。您已经调整并保存过的设计，可以从它开始而不必从零开始。"
    ),
  },
  {
    id: "m-patient",
    skipWhenMissing: TOUR_TARGETS_MOBILE.patient,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.patient),
    reveal: {
      open: TOUR_TARGETS_MOBILE.patient,
      close: "panel",
      holdMs: 3200,
      tabs: [TOUR_PANEL_TARGETS.patientTabPressure, TOUR_PANEL_TARGETS.patientTabInfo],
    },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.patient),
    line: L(
      "Patient. Who the insole is for, and the conditions that shape how it is designed. "
        + "And if the design has a pressure scan, this same panel has a second tab, with the pressure examination of the foot.",
      "Patiënt. Voor wie de zool is, en de aandoeningen die het ontwerp bepalen. "
        + "En als het ontwerp een drukscan heeft, heeft dit zelfde paneel een tweede tabblad, met het drukonderzoek van de voet.",
      "بیمار. کفی برای چه کسی است و چه شرایطی طراحی آن را شکل می‌دهد. "
        + "و اگر طرح اسکن فشار داشته باشد، همین پنل یک زبانه دوم هم دارد، با معاینه فشار پا.",
      "患者。鞋垫的使用者，以及决定设计方式的各项状况。"
        + "如果设计带有压力扫描，同一面板还有第二个标签页，显示足部的压力检查。"
    ),
  },
  {
    id: "m-align",
    skipWhenMissing: TOUR_TARGETS_MOBILE.align,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.align),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.align),
    line: L(
      "Align Pressure. Once a pressure scan is loaded, this lines it up with the insole.",
      "Druk uitlijnen. Zodra een drukscan geladen is, legt dit hem op de zool.",
      "هم‌ترازی فشار. وقتی اسکن فشار بارگذاری شد، آن را روی کفی منطبق می‌کند.",
      "对齐压力。压力扫描加载后，用它把数据与鞋垫对齐。"
    ),
  },
  {
    id: "m-model-category",
    skipWhenMissing: TOUR_TARGETS_MOBILE.modelCategory,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.modelCategory),
    reveal: { open: TOUR_TARGETS_MOBILE.modelCategory, close: "toggle", holdMs: 1800 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.modelCategory),
    line: L(
      "The model type. It switches what the design is built on — an insole, a sandal, or a high heel — and rebuilds the model to match.",
      "Het modeltype. Hiermee wissel je waar het ontwerp op gebouwd is — een zool, een sandaal of een hoge hak — en wordt het model opnieuw opgebouwd.",
      "نوع مدل. با آن انتخاب می‌کنید طرح روی چه چیزی ساخته شود — کفی، صندل یا پاشنه‌بلند — و مدل بر همان اساس بازسازی می‌شود.",
      "模型类型。用它切换设计所基于的款式——鞋垫、凉鞋或高跟鞋——模型会随之重建。"
    ),
  },
  {
    id: "m-print",
    skipWhenMissing: TOUR_TARGETS_MOBILE.print,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.print),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.print),
    line: L(
      "Print. This prepares the print file and sends the design to the printing hub.",
      "Printen. Dit maakt het printbestand klaar en stuurt het ontwerp naar de printhub.",
      "چاپ. فایل چاپ را آماده می‌کند و طرح را به هاب چاپ می‌فرستد.",
      "打印。生成打印文件并将设计发送到打印中心。"
    ),
  },
    {
    id: "m-settings",
    skipWhenMissing: TOUR_TARGETS_MOBILE.settings,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.settings),
    reveal: { open: TOUR_TARGETS_MOBILE.settings, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.settings),
    line: L(
      "Settings. This is where the printers live: their addresses, which one is the default for each foot, and the print helpers.",
      "Instellingen. Hier staan de printers: hun adressen, welke standaard is per voet, en de printhulpen.",
      "تنظیمات. چاپگرها اینجا هستند: آدرس‌هایشان، اینکه کدام برای هر پا پیش‌فرض است، و کمک‌های چاپ.",
      "设置。打印机在这里：地址、每只脚的默认打印机，以及打印辅助件。"
    ),
  },
  {
    id: "m-theme",
    skipWhenMissing: TOUR_TARGETS_MOBILE.theme,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.theme),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.theme),
    line: L(
      "Theme. This switches the application between the light and dark appearance.",
      "Thema. Dit wisselt de applicatie tussen de lichte en donkere weergave.",
      "پوسته. برنامه را بین ظاهر روشن و تاریک جابه‌جا می‌کند.",
      "主题。在浅色与深色外观之间切换应用。"
    ),
    reveal: {
      open: TOUR_TARGETS_MOBILE.theme,
      close: "toggle",
      theme: true,
      overlay: TOUR_TARGETS_MOBILE.menu,
      holdMs: 2600,
    },
  },
{
    id: "m-save",
    skipWhenMissing: TOUR_TARGETS_MOBILE.save,
    setup: reopenFor(TOUR_TARGETS_MOBILE.menu, TOUR_TARGETS_MOBILE.save),
    reveal: { open: TOUR_TARGETS_MOBILE.save, close: "toggle", holdMs: 1800 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.save),
    line: L(
      "And Save, where a finished design leaves the app as a file or is kept as a template.",
      "En Opslaan, waar een afgerond ontwerp de app verlaat als bestand of als sjabloon wordt bewaard.",
      "و ذخیره، جایی که طرح نهایی به‌صورت فایل خارج می‌شود یا به‌عنوان قالب می‌ماند.",
      "以及保存，完成的设计从这里导出为文件或保存为模板。"
    ),
    after: async () => {
      closeMobileOverlays();
      clearHighlights();
      await waitMs(160);
    },
  },
  {
    id: "m-edit-intro",
    skipWhenMissing: TOUR_TARGETS_MOBILE.editMenu,
    setup: async () => {
      // The drawer and the two menus stack rather than replace one another, so
      // the previous section is dismissed before this one opens anything.
      closeMobileOverlays();
      await waitMs(240);
    },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.editMenu),
    line: L(
      "The pencil at the top right is where the design is actually shaped. Let me open it.",
      "Het potlood rechtsboven is waar het ontwerp echt gevormd wordt. Ik open het even.",
      "مداد بالا سمت راست جایی است که طرح واقعاً شکل می‌گیرد. بازش می‌کنم.",
      "右上角的铅笔是真正塑造设计的地方。我来打开它。"
    ),
    after: async () => {
      clickTarget(TOUR_TARGETS_MOBILE.editMenu);
      await waitMs(260);
    },
  },
  {
    id: "m-tab-view",
    skipWhenMissing: TOUR_TARGETS_MOBILE.tabView,
    setup: reopenFor(TOUR_TARGETS_MOBILE.editMenu, TOUR_TARGETS_MOBILE.tabView),
    reveal: { open: TOUR_TARGETS_MOBILE.tabView, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.tabView),
    line: L(
      "Automatic Design. You give it the patient and the scans, and it shapes a first insole for you.",
      "Automatisch ontwerp. Je geeft het de patiënt en de scans, en het vormt een eerste zool voor je.",
      "طراحی خودکار. بیمار و اسکن‌ها را می‌دهید و اولین کفی را برایتان شکل می‌دهد.",
      "自动设计。提供患者信息和扫描数据，它会为您生成第一版鞋垫。"
    ),
  },
  {
    id: "m-tab-edit",
    skipWhenMissing: TOUR_TARGETS_MOBILE.tabEdit,
    setup: reopenFor(TOUR_TARGETS_MOBILE.editMenu, TOUR_TARGETS_MOBILE.tabEdit),
    reveal: { open: TOUR_TARGETS_MOBILE.tabEdit, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.tabEdit),
    line: L(
      "Edit Surface. Arch height, heel depth, thickness and wedges, each adjustable by hand.",
      "Oppervlak bewerken. Voetbooghoogte, hieldiepte, dikte en wiggen, elk met de hand instelbaar.",
      "ویرایش سطح. ارتفاع قوس، عمق پاشنه، ضخامت و گوه‌ها، هرکدام با دست قابل تنظیم.",
      "编辑表面。足弓高度、足跟深度、厚度和楔形，每一项都可手动调整。"
    ),
  },
  {
    id: "m-tab-additions",
    skipWhenMissing: TOUR_TARGETS_MOBILE.tabAdditions,
    setup: reopenFor(TOUR_TARGETS_MOBILE.editMenu, TOUR_TARGETS_MOBILE.tabAdditions),
    reveal: { open: TOUR_TARGETS_MOBILE.tabAdditions, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.tabAdditions),
    line: L(
      "Additions. The orthopedic corrections you place onto the insole.",
      "Toevoegingen. De orthopedische correcties die je op de zool plaatst.",
      "افزودنی‌ها. اصلاحات ارتوپدی که روی کفی قرار می‌دهید.",
      "附加件。放置在鞋垫上的矫形修正。"
    ),
  },
  {
    id: "m-tab-engraving",
    skipWhenMissing: TOUR_TARGETS_MOBILE.tabEngraving,
    setup: reopenFor(TOUR_TARGETS_MOBILE.editMenu, TOUR_TARGETS_MOBILE.tabEngraving),
    reveal: { open: TOUR_TARGETS_MOBILE.tabEngraving, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.tabEngraving),
    line: L(
      "Engraving. This cuts text or a logo into the insole itself.",
      "Graveren. Dit snijdt tekst of een logo in de zool zelf.",
      "حکاکی. متن یا لوگو را روی خود کفی حک می‌کند.",
      "雕刻。在鞋垫本身刻上文字或标志。"
    ),
  },
  {
    id: "m-tab-project",
    skipWhenMissing: TOUR_TARGETS_MOBILE.tabProject,
    setup: reopenFor(TOUR_TARGETS_MOBILE.editMenu, TOUR_TARGETS_MOBILE.tabProject),
    // Held a little shorter than the four tab steps before it. Its reveal is
    // silence that sits between this section and the eye, and by the fifth
    // panel the pattern is familiar.
    reveal: { open: TOUR_TARGETS_MOBILE.tabProject, close: "panel", holdMs: 2000 },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.tabProject),
    line: PROJECT_SETTINGS_LINE,
    // A long line, and the eye section follows it: trim the leftover audio
    // timeline so that handover is not spent in silence.
    tightenHandoff: true,
    after: async () => {
      closeMobileOverlays();
      clearHighlights();
    },
  },
  {
    id: "m-view-intro",
    skipWhenMissing: TOUR_TARGETS_MOBILE.viewMenu,
    setup: async () => {
      // The drawer and the two menus stack rather than replace one another, so
      // the previous section is dismissed before this one opens anything. Kept
      // even though the project step already closes them, because that step
      // drops itself on a design that has no project tab. Short settle: this
      // one sits in the silence between the two sections.
      closeMobileOverlays();
      await waitMs(140);
    },
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.viewMenu),
    line: L(
      "The eye beside it changes how you see the model. It never changes the design, only your view of it. Let me open it.",
      "Het oog ernaast verandert hoe je het model ziet. Het verandert nooit het ontwerp, alleen je beeld ervan. Ik open het even.",
      "چشمِ کنارش نحوه دیدن مدل را عوض می‌کند. هرگز خود طرح را تغییر نمی‌دهد، فقط نمای شما را. بازش می‌کنم.",
      "旁边的眼睛改变您观察模型的方式，它从不改变设计本身，只改变视角。我来打开它。"
    ),
    after: async () => {
      clickTarget(TOUR_TARGETS_MOBILE.viewMenu);
      await waitMs(260);
    },
  },
  {
    id: "m-labels",
    skipWhenMissing: TOUR_TARGETS_MOBILE.labels,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.labels),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.labels),
    line: L(
      "Labels. This shows or hides the measurement numbers on the model.",
      "Labels. Dit toont of verbergt de maatgetallen op het model.",
      "برچسب‌ها. اعداد اندازه‌گیری روی مدل را نشان می‌دهد یا پنهان می‌کند.",
      "标签。显示或隐藏模型上的测量数值。"
    ),
    reveal: { open: TOUR_TARGETS_MOBILE.labels, close: "toggle", view: "liveMode", holdMs: 2200 },
  },
  {
    id: "m-coloring",
    skipWhenMissing: TOUR_TARGETS_MOBILE.coloring,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.coloring),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.coloring),
    line: L(
      "Colour. This switches between the plain insole and the pressure analysis.",
      "Kleur. Dit wisselt tussen de gewone zool en de drukanalyse.",
      "رنگ. بین کفی ساده و تحلیل فشار جابه‌جا می‌شود.",
      "颜色。在普通鞋垫与压力分析之间切换。"
    ),
    reveal: {
      open: TOUR_TARGETS_MOBILE.coloring,
      close: "toggle",
      view: "coloring",
      // The pressure recording runs over the colouring, so this one is held
      // longer than the other view controls.
      alongside: { animatePressure: true },
      holdMs: 5200,
      // The mobile item has no disabled state, so the gate is explicit here.
      enabled: hasColouringData,
    },
  },
  {
    id: "m-camera",
    skipWhenMissing: TOUR_TARGETS_MOBILE.camera,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.camera),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.camera),
    line: L(
      "Camera. This swaps between a perspective view and a flat, straight-on one.",
      "Camera. Dit wisselt tussen een perspectiefbeeld en een recht, vlak beeld.",
      "دوربین. بین نمای پرسپکتیو و نمای صاف روبه‌رو جابه‌جا می‌شود.",
      "相机。在透视视图与正对的平面视图之间切换。"
    ),
    reveal: { open: TOUR_TARGETS_MOBILE.camera, close: "toggle", view: "perspectiveCamera", holdMs: 2200 },
  },
  {
    id: "m-wireframe",
    skipWhenMissing: TOUR_TARGETS_MOBILE.wireframe,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.wireframe),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.wireframe),
    line: L(
      "Wireframe. This shows the mesh the insole is actually built from.",
      "Draadmodel. Dit toont het mesh waaruit de zool werkelijk is opgebouwd.",
      "مش سیمی. شبکه‌ای که کفی واقعاً از آن ساخته شده را نشان می‌دهد.",
      "线框。显示构成鞋垫的实际网格。"
    ),
    // insole -> wireframe -> both -> insole.
    reveal: { open: TOUR_TARGETS_MOBILE.wireframe, close: "toggle", view: "wireframe", holdMs: 2200 },
  },
  {
    id: "m-clipping",
    skipWhenMissing: TOUR_TARGETS_MOBILE.clipping,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.clipping),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.clipping),
    line: L(
      "Clipping. This cuts the model open so you can look inside it.",
      "Doorsnede. Dit snijdt het model open zodat je erin kunt kijken.",
      "برش. مدل را باز می‌برد تا داخل آن را ببینید.",
      "剖切。剖开模型，让您查看内部。"
    ),
    reveal: { open: TOUR_TARGETS_MOBILE.clipping, close: "toggle", view: "clipping", holdMs: 2200 },
  },
  {
    id: "m-grid",
    skipWhenMissing: TOUR_TARGETS_MOBILE.grid,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.grid),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.grid),
    line: L(
      "Grid. This lays a measuring grid under the model.",
      "Raster. Dit legt een meetraster onder het model.",
      "شبکه. یک شبکه اندازه‌گیری زیر مدل می‌گذارد.",
      "网格。在模型下方铺一层测量网格。"
    ),
    reveal: { open: TOUR_TARGETS_MOBILE.grid, close: "toggle", view: "grids", holdMs: 2200 },
  },
  {
    id: "m-feet-view",
    skipWhenMissing: TOUR_TARGETS_MOBILE.feetView,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.feetView),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.feetView),
    line: L(
      "This one switches between the left foot, the right foot, or both together.",
      "Deze wisselt tussen de linkervoet, de rechtervoet of beide samen.",
      "این یکی بین پای چپ، پای راست یا هر دو با هم جابه‌جا می‌شود.",
      "这个用于在左脚、右脚或双脚之间切换。"
    ),
    // both -> right -> left -> both.
    reveal: { open: TOUR_TARGETS_MOBILE.feetView, close: "toggle", view: "feetView", holdMs: 2200 },
  },
  {
    id: "m-measurement",
    skipWhenMissing: TOUR_TARGETS_MOBILE.measurement,
    setup: reopenFor(TOUR_TARGETS_MOBILE.viewMenu, TOUR_TARGETS_MOBILE.measurement),
    spotlight: () => highlightTargets(TOUR_TARGETS_MOBILE.measurement),
    line: L(
      "And units, the last of them: this switches the whole application between millimetres and inches.",
      "En eenheden, de laatste: dit schakelt de hele applicatie tussen millimeters en inches.",
      "و واحدها، آخرینشان: کل برنامه را بین میلی‌متر و اینچ جابه‌جا می‌کند.",
      "以及最后的单位：在毫米与英寸之间切换整个应用。"
    ),
    reveal: { open: TOUR_TARGETS_MOBILE.measurement, close: "toggle", view: "measurementSystem", holdMs: 2200 },
    after: async () => {
      closeMobileOverlays();
      clearHighlights();
      await waitMs(200);
    },
  },
];

/**
 * Phase 2d — the bottom bar, in the order the buttons sit in it.
 *
 * On the desktop these are the footer; on mobile the same settings live in the
 * drawer and the view menu, so the mobile variant opens those instead. Tickets
 * has no mobile equivalent at all, so that step drops itself there.
 */
const FOOTER_STEPS: TourStep[] = [
  {
    id: "footer-intro",
    skipWhenBarHidden: ".footer",
    spotlight: () => {
      highlightGroup(".footer");
      highlightTargets([]);
    },
    line: L(
      "Last, the bar along the bottom. It holds the settings that apply to the whole application rather than to one design.",
      "Tot slot de balk onderaan. Daar staan de instellingen die voor de hele applicatie gelden, niet voor één ontwerp.",
      "و آخر، نوار پایین. تنظیماتی آنجاست که به کل برنامه مربوط است، نه به یک طرح.",
      "最后是底部栏。这里是适用于整个应用、而非单个设计的设置。"
    ),
  },
  {
    id: "footer-measurement",
    skipWhenMissing: TOUR_TARGETS.measurement,
    spotlight: () => highlightTargets(TOUR_TARGETS.measurement),
    line: L(
      "Units. This switches the whole application between millimetres and inches.",
      "Eenheden. Dit schakelt de hele applicatie tussen millimeters en inches.",
      "واحدها. کل برنامه را بین میلی‌متر و اینچ جابه‌جا می‌کند.",
      "单位。在毫米与英寸之间切换整个应用。"
    ),
    reveal: { open: TOUR_TARGETS.measurement, close: "toggle", view: "measurementSystem", holdMs: 2200 },
  },
  {
    id: "footer-theme",
    skipWhenMissing: TOUR_TARGETS.theme,
    spotlight: () => highlightTargets(TOUR_TARGETS.theme),
    line: L(
      "Theme. This switches between the light and dark appearance.",
      "Thema. Dit wisselt tussen de lichte en donkere weergave.",
      "پوسته. بین ظاهر روشن و تاریک جابه‌جا می‌شود.",
      "主题。在浅色与深色外观之间切换。"
    ),
    reveal: { open: TOUR_TARGETS.theme, close: "toggle", theme: true, holdMs: 2600 },
  },
  {
    id: "footer-settings",
    skipWhenMissing: TOUR_TARGETS.settings,
    reveal: { open: TOUR_TARGETS.settings, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS.settings),
    line: L(
      "Settings. This is where the printers live: their addresses, which one is the default for each foot, and the print helpers.",
      "Instellingen. Hier staan de printers: hun adressen, welke standaard is per voet, en de printhulpen.",
      "تنظیمات. چاپگرها اینجا هستند: آدرس‌هایشان، اینکه کدام برای هر پا پیش‌فرض است، و کمک‌های چاپ.",
      "设置。打印机在这里：地址、每只脚的默认打印机，以及打印辅助件。"
    ),
  },
  {
    id: "footer-tickets",
    skipWhenMissing: TOUR_TARGETS.tickets,
    reveal: { open: TOUR_TARGETS.tickets, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS.tickets),
    line: L(
      "And Tickets, which is how you send a problem or a request to the support team without leaving the application.",
      "En Tickets, waarmee je een probleem of verzoek naar het supportteam stuurt zonder de applicatie te verlaten.",
      "و تیکت‌ها، که با آن بدون خروج از برنامه یک مشکل یا درخواست را به تیم پشتیبانی می‌فرستید.",
      "以及工单，让您无需离开应用即可向支持团队提交问题或请求。"
    ),
  },
];

/** Asked when the visitor interrupts the demonstration. */
const DEMO_STOP_CONFIRM: TourStep = {
  id: "demo-stop-confirm",
  spotlight: () => {
    clearHighlights();
    highlightGroup(null);
  },
  awaitsAnswer: true,
  answerTimeoutMs: 20000,
  // Carrying on is the default. Ending the tour on a silence meant a visitor who
  // said something the assistant could not act on was left with nothing at all:
  // the script had broken off and the fallback then closed it down. Stopping is
  // still one word away, and the Skip button is always there.
  defaultAnswer: "acceptLesson",
  line: E(
    "All right, I have stopped. Shall I carry on, or would you rather take a look yourself? Just say yes or no."
  ),
};

/**
 * The same choice after the visitor asked a question, without a line of its own.
 *
 * The assistant ends its answer by asking whether to carry on — the system
 * prompt makes that question part of its answer — so the app only listens for
 * the reply. Speaking DEMO_STOP_CONFIRM here as well asked the visitor twice.
 */
const DEMO_QUESTION_CONTINUE: TourStep = {
  ...DEMO_STOP_CONFIRM,
  id: "demo-question-continue",
  // Shorter than after an explicit stop: the visitor has just been asked, and
  // carrying on is the default if no reply comes.
  answerTimeoutMs: 10000,
  // An empty line is spoken as nothing; the step then waits for the answer
  // like any other question.
  line: () => "",
};

/**
 * The exhibition demonstration, start to finish.
 *
 * One linear run with no choice to make: the visitor arrives from a QR code and
 * is shown the product on a prepared model. Each step reveals the thing it is
 * talking about, because the design opens with pressure, bones, shoe and
 * additions all switched off.
 *
 * Interruptible throughout — the microphone stays open, and asking the
 * assistant to stop breaks off the run and asks what they want instead.
 */
/** The sandal step's own sentence. */
const DEMO_SANDAL_LINE =
  "Every change you make to the insole can also be applied to create a sandal or slipper model as well. So from one design, you can have a custom insole and a custom sandal at the same time."

/** Where the finished design goes. Spoken in the same step, over the same turn. */
const DEMO_PRINT_LINE =
  "When the design is finished, click Print or ask me to send it to your chosen printer, including one in your own clinic. The design's print file is sent to the printer to produce your insoles, and you can send either model, the insole or the sandal."

/** Both together: what the step says, and what its circle is timed from. */
const DEMO_SANDAL_STEP_LINE = `${DEMO_SANDAL_LINE} ${DEMO_PRINT_LINE}`;

/**
 * The figure is written out because the line is read aloud, not shown.
 *
 * As "350,000" the voice does not simply say it: in a recorded run the number
 * took 2.9 seconds where the words either side of it imply about 1.4, roughly
 * twice as long as the digits are worth. What ElevenLabs expands a figure into
 * before speaking it is not ours to control, so the script says the words it
 * wants heard and leaves nothing to normalisation.
 */
const DEMO_PRESSURE_LINE =
  "In this example, we’ve already preloaded a pressure scan. The patient walks across a pressure plate, and the software shows the measured gait analysis. The software uses the gait analysis to suggest an initial insole design based on three hundred fifty thousand clinical records and the insoles podiatrists prescribed for these.";

const DEMO_PARAMS_INTRO_LINE =
  "From here, you can shape the insole by talking to me, your voice assistant, or by adjusting it manually with your mouse and keyboard. You control each design parameter, and the model updates as you make changes.";

const DEMO_ADDITIONS_LINE = "You can ask me: “Can you add an MP pad?";

const DEMO_ADDITIONS_CLOSE_LINE =
  "Here it is up close, with its controls open. You can tell me to set its height, hardness and shape while you keep examining your patient. Let’s try another voice command.";

const DEMO_ADDITIONS_MOVE_LINE =
  "Move this addition about 4 millimeters or 1/6 of an inch towards the heel.";

const DEMO_VOICE_CAPABILITIES_LINE =
  "By talking to me you can adjust and apply any height, angle and addition of the insole, but also add engravings on the insole, open the pressure plate section of the software and even add your diagnosis into the patient notes. You can also ask me more general questions like: \"This patient has heel pain. What kind of insole design do you suggest?\"";

const DEMO_HANDOVER_LINE =
  "This design is your own copy, and it is stored in this demonstration database, so change anything you like. Or just ask me out loud right now, and I will design it for you.";

const DEMO_TUTORIAL_POINTER_LINE =
  "If you'd like to learn more about the software and its features, just ask me to walk you through the tutorial.";

/**
 * How much of the estimated sandal+print speech the orbit is given.
 *
 * Just under the whole line, so the circle ends with the words rather than
 * spinning on in silence. It used to be 0.82, which was not a choice about the
 * shot but a correction for an estimate that ran a fifth long; now that
 * estimateSpeechMs is measured against the voice, the figure says what it means.
 * stopVoiceOrbit in after still cuts the circle if speech ends early.
 */
const SANDAL_ORBIT_SPEECH_RATIO = 0.97;

/**
 * Camera duration paced from the line it accompanies.
 *
 * Absolute ms break as soon as TTS is a bit faster/slower. A fraction of
 * estimateSpeechMs stays in step when wording or network pace changes, and
 * estimateSpeechMs now corrects itself against the voice, so a ratio here is a
 * real share of the sentence rather than a share of a guess about it.
 */
function cameraMsForLine(
  text: string,
  ratio: number,
  bounds: { min?: number; max?: number } = {}
): number {
  const { min = 800, max = 2200 } = bounds;
  return Math.min(max, Math.max(min, Math.round(estimateSpeechMs(text) * ratio)));
}

/**
 * Spoken as the first SPEAK_EXACTLY line of the tour (not as the session
 * firstMessage). Putting it on firstMessage raced the tour start on phones and
 * left visitors with an open mic after the greeting.
 */
export const EXHIBITION_WELCOME = "Welcome to T-Soles, your voice-assisted software for designing custom orthotic insoles. You can make all design adjustments just by talking to me. I’ll show you how it works on a prepared model.";

const DEMO_STEPS: TourStep[] = [
  {
    id: "demo-welcome",
    line: E(EXHIBITION_WELCOME),
  },
  {
    id: "demo-pressure",
    // Camera and reveal both belong to this sentence, so both run on its first
    // audible frame. In setup they happened before the line was even sent, which
    // is why the view swung round while the assistant was still silent.
    spotlight: () => {
      // Overhead is where a pressure map reads: it is a plan view of the foot.
      // Short early swing — finishes well before the long gait explanation ends.
      void fitCameraToVisible({
        view: "top",
        durationMs: cameraMsForLine(DEMO_PRESSURE_LINE, 0.085, { min: 1200, max: 1800 }),
        fill: 0.82,
      });
      dispatchViewerMode("coloring", "pressure");
    },
    line: E(DEMO_PRESSURE_LINE),
    after: async () => {
      // Do not nudge here: sendUserActivity interrupts leftover TTS and cuts the
      // end of this long line ("350,000 clinical records…").
      clearLocalSpeechState();
    },
  },
  {
    id: "demo-params-intro",
    // Leave pressure view as speech starts — awaiting the full camera here
    // delayed SPEAK_EXACTLY and the agent dropped the line.
    setup: async () => {
      dispatchViewerMode("coloring", "color");
    },
    spotlight: () => {
      void fitCameraToVisible({
        view: "reset",
        durationMs: cameraMsForLine(DEMO_PARAMS_INTRO_LINE, 0.073, { min: 850, max: 1200 }),
        fill: 0.72,
      });
    },
    line: E(DEMO_PARAMS_INTRO_LINE),
  },
  {
    // The pads appear here and are talked about from a distance. The trip in is a
    // step of its own so that it happens on the words that announce it.
    id: "demo-additions",
    after: async () => {
      dispatchProjectSetting("additionsMesh", "on");
      await waitMs(200);
    },
    line: E(DEMO_ADDITIONS_LINE),
  },
  {
    // Close-up and the move command are one SPEAK_EXACTLY. Splitting them after
    // a trailing "command:" left the agent holding the turn; the tour then
    // advanced into voice-capabilities (camera pulled back) before the delayed
    // move line finally played.
    id: "demo-additions-close",
    spotlight: () => {
      const target = firstDemoAddition();
      if (!target) return;
      additionFocusRun = focusVoiceAddition({
        foot: target.side,
        index: target.index,
        fill: 0.42,
        openLabel: true,
        keepCamera: true,
        holdMs: 400,
      });
    },
    line: E(
      `${DEMO_ADDITIONS_CLOSE_LINE} ${DEMO_ADDITIONS_MOVE_LINE}`
    ),
    tightenHandoff: true,
    after: async () => {
      // Don't block the next step on the focus promise — camera already stays
      // via keepCamera.
      additionFocusRun = null;
      // The command is spoken, and only then obeyed, the way it would be if a
      // visitor had said it. Then a beat on the moved pad before the next line
      // takes the camera back out, so the one thing this step exists to show is
      // not over the moment it happens.
      moveFirstAdditionForDemo();
      await waitMs(1000);
      clearLocalSpeechState();
    },
  },
  {
    id: "demo-voice-capabilities",
    // Pull back from the addition close-up so the visitor sees the whole insole
    // again while the capabilities are listed.
    //
    // On the first audible frame, and not awaited. In `setup` the same two moves
    // ran before the line was even sent, so the camera jumped out of the
    // close-up into silence — and, coming straight after the pad had moved, it
    // read as the tour snatching the model away. Riding the opening words
    // instead turns it into one continuous shot, and the second or so it used to
    // spend in dead air is simply gone.
    spotlight: () => {
      void (async () => {
        try {
          await releaseVoiceFocus({ keepCamera: true });
          await fitCameraToVisible({
            view: "reset",
            // A long line and a long way back: unhurried, rather than the snap a
            // fixed second gave it.
            durationMs: cameraMsForLine(DEMO_VOICE_CAPABILITIES_LINE, 0.09, { min: 1400, max: 2400 }),
            fill: 0.72,
          });
        } catch {
          voiceLog("voiceCapabilities:cameraResetFailed");
        }
      })();
    },
    line: E(DEMO_VOICE_CAPABILITIES_LINE),
    tightenHandoff: true,
    after: async () => {
      clearLocalSpeechState();
    },
  },
  {
    // The same design shown as a sandal. Switched in setup rather than in
    // spotlight because the model is rebuilt: starting the circle in the same
    // tick would frame the insole that is being replaced.
    // The print explanation is spoken here too, over the same turn of the
    // camera. As two steps the print sentence had to wait for the circle to
    // finish, which was a long silence with nothing happening in it.
    id: "demo-sandal",
    setup: async () => {
      // Camera already reset in demo-voice-capabilities. Long rebuild: mic must
      // stay deaf (deafMic) or ambient noise interrupts the SPEAK_EXACTLY that
      // follows and the sandal step looks broken.
      try {
        holdVoiceCamera(true);
        const scene = captureSceneVisibility();
        setModelCategory("sandal");
        await waitForCategory("Sandal");
        restoreSceneVisibility(scene);
      } catch {
        voiceLog("sandal:setupFailed");
        holdVoiceCamera(false);
      }
    },
    spotlight: () => {
      sandalOrbitRun = orbitVoiceCamera({
        degrees: 360,
        durationMs: Math.max(
          12000,
          Math.round(estimateSpeechMs(DEMO_SANDAL_STEP_LINE) * SANDAL_ORBIT_SPEECH_RATIO)
        ),
        reframe: false,
        tiltDegrees: 45,
      }).catch(() => {
        voiceLog("sandal:orbitFailed");
      });
    },
    line: E(DEMO_SANDAL_STEP_LINE),
    // Long combined line over-queues the audio timeline; without this the gap
    // before handover is several silent seconds after speech has already ended.
    tightenHandoff: true,
    after: async () => {
      // Stop the circle without waiting on it — speech is done and the next
      // line should start immediately.
      try {
        stopVoiceOrbit();
      } catch {
        voiceLog("sandal:afterFailed");
      } finally {
        sandalOrbitRun = null;
        holdVoiceCamera(false);
        clearHighlights();
        clearLocalSpeechState();
      }
    },
  },
  {
    id: "demo-handover",
    spotlight: () => {
      clearHighlights();
      highlightGroup(null);
    },
    line: E(DEMO_HANDOVER_LINE),
  },
  {
    // Tutorial pointer: speak first, then open / bold. Opening the mobile menu
    // in setup lit the UI while the agent was still silent.
    id: "demo-tutorial-pointer",
    spotlight: () => {
      highlightGroup(null);
      // Voice session forces labels on; turn them off so Labels is not the
      // highlighted/on item when this menu opens to point at Tutorial.
      dispatchViewerMode("labels", "off");
      if (isMobileLayout()) {
        closeMobileOverlays();
        clickTarget(TOUR_TARGETS_MOBILE.viewMenu);
        // Popover needs a tick before the Tutorial row exists to highlight.
        window.setTimeout(() => {
          highlightTargets(TOUR_TARGETS_MOBILE.tutorial);
        }, 280);
      } else {
        highlightTargets(TOUR_TARGETS.tutorial);
      }
    },
    line: E(DEMO_TUTORIAL_POINTER_LINE),
    after: async () => {
      // On mobile the eye-menu popover was opened so Tutorial could be lit. Leave
      // it up briefly after speech so the visitor sees the target, then close it.
      if (!isMobileLayout()) return;
      await waitMs(900);
      closeMobileOverlays();
      clearHighlights();
      highlightGroup(null);
    },
  },
];

/**
 * Wait for the viewer to finish rebuilding after a model change.
 *
 * Switching between the insole and the sandal replaces the meshes, and the
 * category on the design is set as part of that. Polling it is what keeps the
 * camera from circling a model that is still being taken apart. Gives up after a
 * few seconds rather than stalling the narration on a rebuild that never lands.
 */
async function waitForCategory(category: string, timeoutMs = 6000): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (useDesignStore.getState().insoleDesign?.category === category) {
      // The category is set before the meshes are finished, so settle after it.
      await waitMs(350);
      return;
    }
    await waitMs(80);
  }
}

/**
 * After a clean tour finish the scene is often still on the sandal. Switch back
 * to the insole before handing the mic to normal mode so free-form design work
 * starts on the orthotic, not the sandal mesh.
 */
async function restoreInsoleAfterTour(): Promise<void> {
  const category = useDesignStore.getState().insoleDesign?.category;
  if (!category || category === "Insole") return;
  try {
    stopVoiceOrbit();
    sandalOrbitRun = null;
    holdVoiceCamera(true);
    const scene = captureSceneVisibility();
    setModelCategory("insole");
    await waitForCategory("Insole");
    restoreSceneVisibility(scene);
    holdVoiceCamera(false);
    // Let the restored visibility reach the meshes before anything is measured.
    //
    // The framing comes from the meshes that are visible at the moment it is
    // asked for, and restoreSceneVisibility only writes the store — the viewer
    // applies those flags on a later render. Framing in the same tick therefore
    // measured the scene as it still was, sandal mesh included, and a box that
    // size puts the camera a long way back from the insoles. That is the gap
    // this beat closes; waitForCategory's own settle covers the rebuild, not
    // this.
    await waitMs(300);
    // The same framing the tour opened on: the initial view's direction, fitted
    // to the insoles that are visible now. Deliberately not the named reset
    // view, which restores the camera wholesale including a distance measured
    // when other meshes were in the scene.
    void fitCameraToVisible({ view: "reset", durationMs: 700, fill: 0.72 });
  } catch {
    holdVoiceCamera(false);
    voiceLog("tour:restoreInsoleFailed");
  }
}

/** In-flight circle around the sandal, so its step can wait for the full turn. */
let sandalOrbitRun: Promise<void> | null = null;

/** What the scene is showing, so a model rebuild does not lose it. */
type SceneVisibility = {
  coloring: boolean;
  insolesVisible: boolean;
  additionsViewMeshVisible: boolean;
  bonesMeshVisible: boolean;
  shoeDataMeshVisible: boolean;
  liftedInsoleMeshVisible: boolean;
  feetView: string;
};
function captureSceneVisibility(): SceneVisibility {
  const v = useViewerStore.getState();
  return {
    coloring: v.coloring,
    insolesVisible: v.insolesVisible,
    additionsViewMeshVisible: v.additionsViewMeshVisible,
    bonesMeshVisible: v.bonesMeshVisible,
    shoeDataMeshVisible: v.shoeDataMeshVisible,
    liftedInsoleMeshVisible: v.liftedInsoleMeshVisible,
    feetView: v.feetView,
  };
}

function restoreSceneVisibility(saved: SceneVisibility): void {
  useViewerStore.setState({ ...saved } as any);
}

/** In-flight camera trip to the addition, so its step can wait for it. */
let additionFocusRun: Promise<void> | null = null;

/** Index of the step currently being run, so an interruption knows where it was. */
let stepCursor = 0;
/**
 * Index, in the steps runSteps is working through, of the last step whose line
 * was spoken to the end. A break that comes after it — in the pause following
 * the line — resumes at the next step rather than repeating what was just heard.
 */
let lastSpokenIndex = -1;

/** The pad this page load has already slid, so a replay does not slide it twice. */
let demoAdditionMove: { side: number; index: number; dx: number; dz: number } | null = null;

/** Must match demo-additions-move SPEAK_EXACTLY line (4 mm ≈ 1/6 in). */
const DEMO_ADDITION_MOVE_MM = 4;

/** The pad the demonstration talks about: the first one it can find. */
function firstDemoAddition(): { side: number; index: number } | null {
  const design = useDesignStore.getState().insoleDesign;
  for (const side of [1, 0]) {
    const additions = design?.insoles?.[side]?.additions ?? [];
    const index = additions.findIndex((addition: any) => addition);
    if (index >= 0) return { side, index };
  }
  return null;
}

function moveFirstAdditionForDemo(): void {
  // Once per page load. Each move is relative on the joint sphere, so replaying
  // the step without this guard would nudge the same pad again.
  if (demoAdditionMove) return;
  const target = firstDemoAddition();
  if (!target) return;
  const foot = target.side === 0 ? "right" : "left";
  moveVoiceAddition(foot, "heel", DEMO_ADDITION_MOVE_MM, undefined, target.index);
  demoAdditionMove = {
    side: target.side,
    index: target.index,
    dx: 0,
    dz: -DEMO_ADDITION_MOVE_MM,
  };
}

/** The standalone tutorial's own opening, in place of the exhibition welcome. */
const TUTORIAL_OPENING: TourStep[] = [
  {
    id: "tutorial-opening",
    spotlight: () => {
      clearHighlights();
      highlightGroup(null);
    },
    line: L(
      "Let me walk you through the interface. I will name each part of the screen and highlight it as I go, so you can see what everything does.",
      "Ik neem je mee door de interface. Ik noem elk deel van het scherm en licht het op terwijl ik ga, zodat je ziet wat alles doet.",
      "بگذارید رابط کاربری را برایتان مرور کنم. هر بخش از صفحه را نام می‌برم و همان لحظه برجسته می‌کنم تا ببینید هر چیزی چه کار می‌کند.",
      "让我带您了解界面。我会逐一说明屏幕各部分并同时高亮显示，让您看清每项功能。"
    ),
  },
];

/** The standalone tutorial's closing line. */
const TUTORIAL_CLOSING: TourStep = {
  id: "tutorial-closing",
  spotlight: () => {
    clearHighlights();
    highlightGroup(null);
  },
  line: L(
    "That is the whole interface. You can ask me for any of it out loud at any time, and I will do it for you.",
    "Dat is de hele interface. Je kunt me er op elk moment hardop om vragen, en ik doe het voor je.",
    "این کل رابط کاربری بود. هر وقت خواستید می‌توانید بلند از من بخواهید و من برایتان انجام می‌دهم.",
    "这就是全部界面。您随时可以开口要求其中任何一项，我会为您完成。"
  ),
};

/**
 * Phase 2 — the three bars, one icon at a time.
 *
 * Exactly one target is lit per step. Lighting a pair and describing both in
 * one sentence left the visitor unsure which icon the words belonged to.
 *
 * Buttons that are not on screen for this visitor — the admin-only and dev-only
 * entries — simply light nothing, because highlightTargets ignores ids it
 * cannot find. Their steps are skipped via `skipWhenMissing`.
 */
const BAR_STEPS: TourStep[] = [
  {
    id: "bar-top-intro",
    skipWhenBarHidden: TOUR_BARS.top,
    spotlight: () => {
      highlightGroup(TOUR_BARS.top);
      highlightTargets([]);
    },
    line: L(
      "The bar along the top is where a design begins and ends. Let me take you through it, one button at a time.",
      "De balk bovenaan is waar een ontwerp begint en eindigt. Ik neem je er knop voor knop doorheen.",
      "نوار بالا جایی است که یک طرح شروع و تمام می‌شود. یکی‌یکی برایتان توضیح می‌دهم.",
      "顶部工具栏是设计的起点和终点。我会逐个按钮为您介绍。"
    ),
  },
  {
    id: "bar-top-database",
    skipWhenMissing: TOUR_TARGETS.database,
    reveal: { open: TOUR_TARGETS.database, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS.database),
    line: L(
      "Database. Every design you save lives here, and this is where you search them and open one again.",
      "Database. Elk ontwerp dat je opslaat staat hier, en hier zoek je ze op en open je er weer een.",
      "دیتابیس. هر طرحی که ذخیره می‌کنید اینجاست و از همین‌جا جستجو و دوباره باز می‌شود.",
      "数据库。您保存的每个设计都在这里，也在这里搜索并重新打开。"
    ),
  },
  {
    id: "bar-top-patient",
    skipWhenMissing: TOUR_TARGETS.patient,
    reveal: {
      open: TOUR_TARGETS.patient,
      close: "panel",
      holdMs: 3200,
      tabs: [TOUR_PANEL_TARGETS.patientTabPressure, TOUR_PANEL_TARGETS.patientTabInfo],
    },
    spotlight: () => highlightTargets(TOUR_TARGETS.patient),
    line: L(
      "Patient Info. Who the insole is for, and the conditions that shape how it is designed. "
        + "And if the design has a pressure scan, this same panel has a second tab, with the pressure examination of the foot.",
      "Patiëntgegevens. Voor wie de zool is, en de aandoeningen die het ontwerp bepalen. "
        + "En als het ontwerp een drukscan heeft, heeft dit zelfde paneel een tweede tabblad, met het drukonderzoek van de voet.",
      "اطلاعات بیمار. کفی برای چه کسی است و چه شرایطی طراحی آن را شکل می‌دهد. "
        + "و اگر طرح اسکن فشار داشته باشد، همین پنل یک زبانه دوم هم دارد، با معاینه فشار پا.",
      "患者信息。鞋垫的使用者，以及决定设计方式的各项状况。"
        + "如果设计带有压力扫描，同一面板还有第二个标签页，显示足部的压力检查。"
    ),
  },
  {
    id: "bar-top-scan",
    skipWhenMissing: TOUR_TARGETS.pressureImport,
    spotlight: () => highlightTargets(TOUR_TARGETS.pressureImport),
    line: L(
      "Load Scan. This is how measurements of the real foot come into the design, and it holds two different scans. Let me open it.",
      "Scan laden. Zo komen metingen van de echte voet in het ontwerp, en er zitten twee verschillende scans in. Ik open hem even.",
      "بارگذاری اسکن. اندازه‌گیری‌های پای واقعی از همین‌جا وارد طرح می‌شود و دو نوع اسکن دارد. بازش می‌کنم.",
      "加载扫描。真实足部的测量数据由此进入设计，其中包含两种扫描。我来打开它。"
    ),
    // Open the dropdown so the two scans below have something to point at.
    after: async () => {
      clickTarget(TOUR_TARGETS.pressureImport);
      await waitMs(200);
    },
  },
  {
    id: "bar-top-scan-pressure",
    skipWhenMissing: TOUR_TARGETS.tsolesImport,
    spotlight: () => highlightTargets(TOUR_TARGETS.tsolesImport),
    line: L(
      "The first is the pressure scan. The patient stands or walks on a pressure plate, and it records where their weight actually falls.",
      "De eerste is de drukscan. De patiënt staat of loopt op een drukplaat, die vastlegt waar het gewicht werkelijk valt.",
      "اولی اسکن فشار است. بیمار روی صفحه فشار می‌ایستد یا راه می‌رود و ثبت می‌شود وزن واقعاً کجا می‌افتد.",
      "第一种是压力扫描。患者站立或行走于压力板上，记录体重实际落在何处。"
    ),
  },
  {
    id: "bar-top-scan-wbct",
    skipWhenMissing: TOUR_TARGETS.wbct,
    spotlight: () => highlightTargets(TOUR_TARGETS.wbct),
    line: L(
      "The second is the W B C T scan. That is a weight bearing C T, and it shows the bones of the foot under the patient's own body weight.",
      "De tweede is de W B C T scan. Dat is een belaste C T, en die toont de botten van de voet onder het eigen lichaamsgewicht.",
      "دومی اسکن دبلیو‌بی‌سی‌تی است، یعنی سی‌تی وزن‌گذار، که استخوان‌های پا را زیر وزن بدن خود بیمار نشان می‌دهد.",
      "第二种是 W B C T 扫描，即负重 C T，显示患者在自身体重下的足部骨骼。"
    ),
    // Put the dropdown away before moving along the bar.
    after: async () => {
      clickTarget(TOUR_TARGETS.pressureImport);
      await waitMs(200);
    },
  },
  {
    id: "bar-top-template",
    skipWhenMissing: TOUR_TARGETS.loadTemplate,
    reveal: { open: TOUR_TARGETS.loadTemplate, close: "panel" },
    spotlight: () => highlightTargets(TOUR_TARGETS.loadTemplate),
    line: L(
      "Load Template. A template is a design you have already shaped and saved, so you can start from it instead of from scratch.",
      "Sjabloon laden. Een sjabloon is een ontwerp dat je al gevormd en opgeslagen hebt, zodat je daarvandaan begint in plaats van bij nul.",
      "بارگذاری قالب. قالب طرحی است که قبلاً شکل داده و ذخیره کرده‌اید تا به‌جای شروع از صفر از آن شروع کنید.",
      "加载模板。模板是您已经调整并保存过的设计，可以从它开始而不必从零开始。"
    ),
  },
  {
    id: "bar-top-align",
    skipWhenMissing: TOUR_TARGETS.alignPressure,
    spotlight: () => highlightTargets(TOUR_TARGETS.alignPressure),
    line: L(
      "Align. Once a pressure scan is loaded, this is how you line it up with the insole so the data sits exactly where the foot does.",
      "Uitlijnen. Zodra een drukscan geladen is, leg je die hiermee op de zool zodat de data precies onder de voet valt.",
      "هم‌ترازی. وقتی اسکن فشار بارگذاری شد، با این آن را روی کفی منطبق می‌کنید تا داده دقیقاً جای پا بنشیند.",
      "对齐。压力扫描加载后，用它把数据与鞋垫对齐，使其正好落在足部位置。"
    ),
  },
  {
    id: "bar-top-save",
    skipWhenMissing: TOUR_TARGETS.output,
    reveal: { open: TOUR_TARGETS.output, close: "toggle", holdMs: 1800 },
    spotlight: () => highlightTargets(TOUR_TARGETS.output),
    line: L(
      "Save. This is where a finished design leaves the app as a file, or is kept as a template for next time.",
      "Opslaan. Hier verlaat een afgerond ontwerp de app als bestand, of wordt het bewaard als sjabloon voor de volgende keer.",
      "ذخیره. از اینجا طرح نهایی به‌صورت فایل از برنامه خارج می‌شود یا برای دفعه بعد به‌عنوان قالب می‌ماند.",
      "保存。完成的设计从这里导出为文件，或保存为模板供下次使用。"
    ),
  },
  {
    id: "bar-top-model-category",
    skipWhenMissing: TOUR_TARGETS.modelCategory,
    reveal: { open: TOUR_TARGETS.modelCategory, close: "toggle", holdMs: 1800 },
    spotlight: () => highlightTargets(TOUR_TARGETS.modelCategory),
    line: L(
      "This one is the model type. It switches what the design is built on — an insole, a sandal, or a high heel — and rebuilds the model to match.",
      "Dit is het modeltype. Hiermee wissel je waar het ontwerp op gebouwd is — een zool, een sandaal of een hoge hak — en wordt het model opnieuw opgebouwd.",
      "این مورد نوع مدل است. با آن انتخاب می‌کنید طرح روی چه چیزی ساخته شود — کفی، صندل یا پاشنه‌بلند — و مدل بر همان اساس بازسازی می‌شود.",
      "这个是模型类型。用它切换设计所基于的款式——鞋垫、凉鞋或高跟鞋——模型会随之重建。"
    ),
  },
  {
    id: "bar-top-print",
    skipWhenMissing: TOUR_TARGETS.print,
    spotlight: () => highlightTargets(TOUR_TARGETS.print),
    line: L(
      "And Print, the end of the road. It prepares the print file and sends the design to the printing hub.",
      "En Printen, het eindpunt. Het maakt het printbestand klaar en stuurt het ontwerp naar de printhub.",
      "و چاپ، پایان مسیر. فایل چاپ را آماده می‌کند و طرح را به هاب چاپ می‌فرستد.",
      "以及打印，流程的终点。它生成打印文件并将设计发送到打印中心。"
    ),
  },
  {
    id: "bar-right-intro",
    skipWhenBarHidden: TOUR_BARS.right,
    spotlight: () => {
      highlightGroup(TOUR_BARS.right);
      highlightTargets([]);
    },
    line: L(
      "The bar on the right is where the design is actually shaped. Each tab opens a different set of controls.",
      "De balk rechts is waar het ontwerp echt gevormd wordt. Elk tabblad opent een andere set bedieningen.",
      "نوار سمت راست جایی است که طرح واقعاً شکل می‌گیرد. هر زبانه مجموعه‌ای از کنترل‌ها را باز می‌کند.",
      "右侧栏是真正塑造设计的地方，每个标签页都会打开一组不同的控件。"
    ),
  },
  {
    id: "bar-right-view",
    skipWhenMissing: TOUR_TARGETS.tabView,
    reveal: { open: TOUR_TARGETS.tabView, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS.tabView),
    line: L(
      "Automatic Design. You give it the patient and the scans, and it shapes a first insole for you.",
      "Automatisch ontwerp. Je geeft het de patiënt en de scans, en het vormt een eerste zool voor je.",
      "طراحی خودکار. بیمار و اسکن‌ها را می‌دهید و اولین کفی را برایتان شکل می‌دهد.",
      "自动设计。提供患者信息和扫描数据，它会为您生成第一版鞋垫。"
    ),
  },
  {
    id: "bar-right-edit",
    skipWhenMissing: TOUR_TARGETS.tabEdit,
    reveal: { open: TOUR_TARGETS.tabEdit, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS.tabEdit),
    line: L(
      "Edit Surface. This is the hand control: arch height, heel depth, thickness, wedges, each one adjustable yourself.",
      "Oppervlak bewerken. Dit is de handbediening: voetbooghoogte, hieldiepte, dikte en wiggen, elk zelf instelbaar.",
      "ویرایش سطح. کنترل دستی است: ارتفاع قوس، عمق پاشنه، ضخامت و گوه‌ها، هرکدام قابل تنظیم توسط خودتان.",
      "编辑表面。这是手动控制：足弓高度、足跟深度、厚度、楔形，每一项都可自行调整。"
    ),
  },
  {
    id: "bar-right-additions",
    skipWhenMissing: TOUR_TARGETS.tabAdditions,
    reveal: { open: TOUR_TARGETS.tabAdditions, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS.tabAdditions),
    line: L(
      "Additions. These are the orthopedic corrections you place onto the insole, each with its own height and hardness.",
      "Toevoegingen. Dit zijn de orthopedische correcties die je op de zool plaatst, elk met eigen hoogte en hardheid.",
      "افزودنی‌ها. اصلاحات ارتوپدی که روی کفی می‌گذارید، هرکدام با ارتفاع و سختی خودش.",
      "附加件。放置在鞋垫上的矫形修正，每个都有自己的高度和硬度。"
    ),
  },
  {
    id: "bar-right-engraving",
    skipWhenMissing: TOUR_TARGETS.tabEngraving,
    reveal: { open: TOUR_TARGETS.tabEngraving, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS.tabEngraving),
    line: L(
      "Engraving. This cuts text or a logo into the insole itself, so a finished pair carries a name.",
      "Graveren. Dit snijdt tekst of een logo in de zool zelf, zodat een afgerond paar een naam draagt.",
      "حکاکی. متن یا لوگو را روی خود کفی حک می‌کند تا یک جفت آماده نام داشته باشد.",
      "雕刻。在鞋垫本身刻上文字或标志，让成品带有名称。"
    ),
  },
  {
    id: "bar-right-project",
    skipWhenMissing: TOUR_TARGETS.tabProject,
    reveal: { open: TOUR_TARGETS.tabProject, close: "panel", holdMs: 2600 },
    spotlight: () => highlightTargets(TOUR_TARGETS.tabProject),
    line: PROJECT_SETTINGS_LINE,
    // A long line, and the eye section follows it: trim the leftover audio
    // timeline so that handover is not spent in silence.
    tightenHandoff: true,
  },
  {
    id: "bar-left-intro",
    skipWhenBarHidden: TOUR_BARS.left,
    spotlight: () => {
      highlightGroup(TOUR_BARS.left);
      highlightTargets([]);
    },
    line: L(
      "The bar on the left changes how you see the model. It never changes the design, only your view of it.",
      "De balk links verandert hoe je het model ziet. Hij verandert nooit het ontwerp, alleen je beeld ervan.",
      "نوار سمت چپ نحوه دیدن مدل را عوض می‌کند. هرگز خود طرح را تغییر نمی‌دهد، فقط نمای شما را.",
      "左侧栏改变您观察模型的方式，它从不改变设计本身，只改变您的视角。"
    ),
  },
  {
    id: "bar-left-labels",
    skipWhenMissing: TOUR_TARGETS.labels,
    spotlight: () => highlightTargets(TOUR_TARGETS.labels),
    line: L(
      "Labels. This shows or hides the measurement numbers on the model.",
      "Labels. Dit toont of verbergt de maatgetallen op het model.",
      "برچسب‌ها. اعداد اندازه‌گیری روی مدل را نشان می‌دهد یا پنهان می‌کند.",
      "标签。显示或隐藏模型上的测量数值。"
    ),
    reveal: { open: TOUR_TARGETS.labels, close: "toggle", view: "liveMode", holdMs: 2200 },
  },
  {
    id: "bar-left-coloring",
    skipWhenMissing: TOUR_TARGETS.coloring,
    spotlight: () => highlightTargets(TOUR_TARGETS.coloring),
    line: L(
      "Colour. This switches between the plain insole and the pressure analysis, once a pressure scan exists.",
      "Kleur. Dit wisselt tussen de gewone zool en de drukanalyse, zodra er een drukscan is.",
      "رنگ. بین کفی ساده و تحلیل فشار جابه‌جا می‌شود، به‌شرط وجود اسکن فشار.",
      "颜色。在普通鞋垫与压力分析之间切换，前提是已有压力扫描。"
    ),
    // Disabled without a scan, so `isTargetActionable` already passes this over.
    // Held longer than the other view controls: the pressure recording is
    // playing over it, and a couple of seconds is not enough to read it.
    reveal: {
      open: TOUR_TARGETS.coloring,
      close: "toggle",
      view: "coloring",
      alongside: { animatePressure: true },
      holdMs: 5200,
    },
  },
  {
    id: "bar-left-camera",
    skipWhenMissing: TOUR_TARGETS.camera,
    spotlight: () => highlightTargets(TOUR_TARGETS.camera),
    line: L(
      "Camera. This swaps between a perspective view and a flat, straight-on one for judging shape.",
      "Camera. Dit wisselt tussen een perspectiefbeeld en een recht, vlak beeld om vorm te beoordelen.",
      "دوربین. بین نمای پرسپکتیو و نمای صاف روبه‌رو برای سنجش فرم جابه‌جا می‌شود.",
      "相机。在透视视图与正对的平面视图之间切换，便于判断形状。"
    ),
    reveal: { open: TOUR_TARGETS.camera, close: "toggle", view: "perspectiveCamera", holdMs: 2200 },
  },
  {
    id: "bar-left-wireframe",
    skipWhenMissing: TOUR_TARGETS.wireframe,
    spotlight: () => highlightTargets(TOUR_TARGETS.wireframe),
    line: L(
      "Wireframe. This shows the mesh the insole is actually built from.",
      "Draadmodel. Dit toont het mesh waaruit de zool werkelijk is opgebouwd.",
      "مش سیمی. شبکه‌ای که کفی واقعاً از آن ساخته شده را نشان می‌دهد.",
      "线框。显示构成鞋垫的实际网格。"
    ),
    // insole -> wireframe -> both -> insole.
    reveal: { open: TOUR_TARGETS.wireframe, close: "toggle", view: "wireframe", holdMs: 2200 },
  },
  {
    id: "bar-left-clipping",
    skipWhenMissing: TOUR_TARGETS.clipping,
    spotlight: () => highlightTargets(TOUR_TARGETS.clipping),
    line: L(
      "Clipping. This cuts the model open so you can look inside it and see the infill.",
      "Doorsnede. Dit snijdt het model open zodat je erin kunt kijken en de vulling ziet.",
      "برش. مدل را باز می‌برد تا داخل آن و ساختار پرکننده را ببینید.",
      "剖切。剖开模型，让您查看内部及填充结构。"
    ),
    reveal: { open: TOUR_TARGETS.clipping, close: "toggle", view: "clipping", holdMs: 2200 },
  },
  {
    id: "bar-left-grid",
    skipWhenMissing: TOUR_TARGETS.grid,
    spotlight: () => highlightTargets(TOUR_TARGETS.grid),
    line: L(
      "Grid. This lays a measuring grid under the model so you can judge size at a glance.",
      "Raster. Dit legt een meetraster onder het model zodat je de maat in één oogopslag ziet.",
      "شبکه. یک شبکه اندازه‌گیری زیر مدل می‌گذارد تا اندازه را در یک نگاه بسنجید.",
      "网格。在模型下方铺一层测量网格，便于一眼判断尺寸。"
    ),
    reveal: { open: TOUR_TARGETS.grid, close: "toggle", view: "grids", holdMs: 2200 },
  },
  {
    id: "bar-left-feet",
    skipWhenMissing: TOUR_TARGETS.feetView,
    spotlight: () => highlightTargets(TOUR_TARGETS.feetView),
    line: L(
      "And this one switches between the left foot, the right foot, or both together.",
      "En deze wisselt tussen de linkervoet, de rechtervoet of beide samen.",
      "و این یکی بین پای چپ، پای راست یا هر دو با هم جابه‌جا می‌شود.",
      "这个用于在左脚、右脚或双脚之间切换。"
    ),
    // both -> right -> left -> both.
    reveal: { open: TOUR_TARGETS.feetView, close: "toggle", view: "feetView", holdMs: 2200 },
  },
];

/** Phase 3 — the branch. The agent answers this with tourControl. */
/** Closing line, shared by the accepted and declined paths. */
// ───────────────────────────────────────────────────────────── engine

type Resolver = (action: TourControlAction) => void;

let runToken = 0;
let running = false;
let answerResolver: Resolver | null = null;
let currentLine = "";
/** Set while a step is being spoken, so "repeat" can re-run the same step. */
let repeatRequested = false;
let skipRequested = false;
/**
 * The visitor asked the assistant to stop while it was talking.
 *
 * Distinct from skipRequested, which ends the tour outright. This one only
 * breaks off the current section so the tour can ask what they want instead.
 */
let stopRequested = false;
/** True when the break was a question the assistant is about to answer. */
let answeringQuestion = false;
/** The most recent answer to an awaitsAnswer step, read by the caller. */
let lastAnswer: TourControlAction | null = null;

export function isTourRunning(): boolean {
  return running;
}

/**
 * Answer a step that is waiting, or steer a step that is speaking. Returns the
 * sentence the agent should say back, or an empty string when it should stay
 * quiet and let the script continue.
 */
export function handleTourControl(action: TourControlAction, ctx?: TourContext): string {
  // Replay and Skip tour by voice go through the very events the buttons fire,
  // rather than calling the tour directly. That is what makes them behave
  // identically: the button path is also what marks the narration as running,
  // which is what takes the controls off the screen while it speaks, and what
  // closes the session and puts them away on a skip.
  if (action === "restart") {
    requestExhibitionTour(true);
    return "";
  }
  // Ending a narration by voice is gone. It only ever worked because the
  // microphone was open between lines, and it is not any more — so a call here
  // can only come from an agent published before the tool was withdrawn. Said
  // plainly rather than ignored, and deliberately not routed into the answer
  // resolver below, which would otherwise swallow it as a break answer.
  if (action === "dismiss") {
    return voiceText({
      en: "Stopping the tour by voice is not available. The visitor can close it with the control on screen.",
      nl: "De rondleiding stoppen met je stem is niet mogelijk. De bezoeker kan die sluiten met de knop op het scherm.",
      fa: "متوقف کردن تور با صدا در دسترس نیست. بازدیدکننده می‌تواند با دکمه‌ی روی صفحه آن را ببندد.",
      zh: "无法通过语音停止导览。访客可以用屏幕上的按钮关闭它。",
    });
  }
  if (!running) {
    return voiceText({
      en: "The tour is not running.",
      nl: "De rondleiding loopt niet.",
      fa: "تور در حال اجرا نیست.",
      zh: "导览当前未在进行。",
    });
  }
  if (action === "skip" || action === "answerQuestion") {
    // Both pause rather than end. skip is "stop talking"; answerQuestion is
    // "I asked you something" — the assistant answers it in its own words, and
    // only afterwards is the visitor asked whether to carry on.
    answeringQuestion = action === "answerQuestion";
    stopRequested = true;
    answerResolver?.("skip");
    answerResolver = null;
    return "";
  }
  if (action === "repeat") {
    repeatRequested = true;
    return "";
  }
  if (answerResolver) {
    answerResolver(action);
    answerResolver = null;
    return "";
  }
  // "next" while a step is mid-sentence: the audio wait below ends on its own,
  // so nothing to do but let it through.
  return "";
}

/** Park until tourControl answers, the tour is stopped, or the visitor says nothing. */
function awaitAnswer(
  timeoutMs = 25000,
  onTimeout: TourControlAction = "declineLesson"
): Promise<TourControlAction> {
  return new Promise((resolve) => {
    answerResolver = resolve;
    window.setTimeout(() => {
      if (answerResolver === resolve) {
        answerResolver = null;
        resolve(onTimeout);
      }
    }, timeoutMs);
  });
}

/**
 * Speak one line and return when the assistant has finished saying it.
 *
 * The estimated duration is only a floor. The real end comes from the output
 * audio going quiet, which is what keeps a highlight lit for exactly as long as
 * the sentence that describes it, however slow the connection is.
 */
/**
 * Put the viewer into the state the tour opens on: a plain insole, with the
 * pressure colour, the WBCT bones and the shoe all switched off.
 *
 * Applied at the start of every run (including mic reopen / Replay). A previous
 * pass may have left the scene on the sandal — without switching back, the
 * welcome would start against the wrong model.
 */
async function resetViewerForTour(): Promise<void> {
  // Every reveal the run makes is undone here, not just the first one. A replay
  // used to restart the words against whatever the previous run had left on
  // screen — bones still showing, one foot hidden, a pad's label open — so the
  // sentences no longer described the picture.
  //
  // The demonstration's own edits to the design are deliberately not touched:
  // those stay where the visitor can see them and are simply never saved.

  stopVoiceOrbit();
  sandalOrbitRun = null;
  additionFocusRun = null;

  // G-code is a different picture altogether: the viewer hides the insoles and
  // puts the printed toolpaths in their place. Nothing here used to undo that,
  // so a visitor who asked to print and then pressed Replay got the script
  // narrating an insole over a scene full of toolpaths. Clearing GcodeReady is
  // what the viewer already watches to take those meshes out of the scene and
  // put the design back, so ask for it here and let it land before the
  // visibility below: that restore turns the bones, the shoe and the pads back
  // on, and whichever of the two writes last is what the run opens with.
  if (useDesignStore.getState().GcodeReady) {
    useDesignStore.setState({ GcodeReady: false });
    await waitMs(220);
  }

  // Whatever the visitor left open over the model — including the Print
  // confirmation, which opens by itself when G-code finishes and has no reason
  // to be sitting over the opening line of a replay.
  openPropertyPanel("close");
  await waitMs(40);

  // Always open on the insole. Closing the mic mid-sandal (or after the sandal
  // step) and opening it again must restart from the beginning of the script
  // against the insole, not keep narrating over the sandal mesh.
  const category = useDesignStore.getState().insoleDesign?.category;
  if (category && category !== "Insole") {
    holdVoiceCamera(true);
    setModelCategory("insole");
    await waitForCategory("Insole");
    holdVoiceCamera(false);
  }

  // "color", not "off": the coloring setting is inverted, and off shows
  // pressure.
  dispatchViewerMode("coloring", "color");
  dispatchProjectSetting("bones", "off");
  dispatchProjectSetting("shoe", "off");
  dispatchProjectSetting("lifted", "off");
  dispatchProjectSetting("additionsMesh", "off");
  dispatchProjectSetting("insoles", "on");
  dispatchViewerMode("feet", "both");
  // A run stopped during the addition close-up leaves a label open and the
  // camera on the pad. keepCamera because the framing below is the one move that
  // should happen, rather than a restore followed by a reframe.
  await releaseVoiceFocus({ keepCamera: true });
  // Camera move must not delay the welcome SPEAK_EXACTLY. Kick it off and
  // continue — awaiting the full animation was ~1s of dead air before speech.
  await waitMs(60);
  void fitCameraToVisible({ view: "reset", durationMs: 500, fill: 0.72 });
}

/** Longest we wait for a leftover turn to go quiet before sending the next line. */
const BETWEEN_LINE_QUIET_MS = 200;

/**
 * Continuous silence that counts as "the agent has stopped talking".
 * Slightly above a breath pause; the min-hold from speech start is the main guard.
 */
const QUIET_SETTLE_MS = 140;

/**
 * Silence that means the line is over.
 *
 * Not a breath: the gap between one sentence's audio and the next's. The
 * assistant's speech is generated a sentence at a time and the player's buffer
 * empties in between, so a short threshold reads that gap as the end of the
 * turn. At 140ms it did, and the next SPEAK_EXACTLY then cut the sentence still
 * playing — which is how the pressure line lost "…350,000 clinical records and
 * the insoles podiatrists prescribed for these." A generous threshold costs
 * about half a second at the end of each step and is what stops that.
 */
const LINE_END_QUIET_MS = 550;

/**
 * The same, before enough of the line can possibly have played.
 *
 * A real gap between two generated sentences and the end of a turn look alike
 * from here — both are the player running out of audio — so the only thing that
 * tells them apart is how much of the line can have been heard by now. Before
 * that point, hold out for longer than any pause measured in a recorded run,
 * the longest of which was 0.85s. It costs nothing when the line ends where it
 * is expected to.
 */
const LINE_STALL_QUIET_MS = 1400;

/**
 * Silence between one step and the next.
 */
const TOUR_STEP_GAP_MS = 80;

/** Mount and open animation, before the hold starts being counted. */
const REVEAL_OPEN_MS = 420;

/** How long an opened panel stays up when its step does not say. */
const REVEAL_HOLD_MS = 2400;

/** Close animation, before the next step is allowed to touch the screen. */
const REVEAL_CLOSE_MS = 340;

/**
 * Settle after a tab is picked, before its hold starts being counted.
 *
 * Longer than a plain content swap would need, because the pressure tab draws
 * its exam onto a canvas: starting the hold on the click meant most of it was
 * spent watching the tab arrive.
 */
const REVEAL_TAB_MS = 480;

/**
 * Close whatever the walkthrough opened.
 *
 * Both halves are needed. The events are the ones the voice assistant already
 * uses, and they reach the toolbar, the footer and the property panel. They do
 * not reach the mobile drawer, which holds its own panels in its own state, so
 * those are dismissed through the backdrop instead.
 *
 * Engraving is left as well as closed, because entering that tab swings the
 * camera round to the engraving view: closing the panel alone would leave the
 * user looking at the insole from an angle they never asked for.
 */
function closeTourPanels(): void {
  document.dispatchEvent(new CustomEvent(VOICE_OPEN_PANEL_EVENT, { detail: { close: true } }));
  document.dispatchEvent(new Event("toolbox:close"));
  document.dispatchEvent(new CustomEvent(VOICE_CLOSE_PANELS_EVENT));
  exitEngravingIfNeeded();
  dismissTopPanel();
}

/**
 * Reopen the overlay a step's item lives in, when something closed it.
 *
 * On a phone every panel is reached through the drawer or one of the two menus,
 * and opening the panel dismisses that overlay — which is what the app itself
 * does when the row is tapped. The next step would then find its own row gone
 * and skip itself, taking the rest of the section with it.
 *
 * Written as a step `setup` rather than as part of the reveal that closed the
 * overlay, so it also covers a panel the user closed themselves and a run that
 * picks up partway through. A step whose item is already on screen pays
 * nothing for it.
 */
function reopenFor(overlay: string, item: string): () => Promise<void> {
  return async () => {
    if (isTargetVisible(item)) return;
    if (isPanelOpen()) {
      closeTourPanels();
      await waitMs(REVEAL_CLOSE_MS);
    }
    clickTarget(overlay);
    await waitMs(320);
  };
}

/**
 * Open what the step described, leave it up, and put it away again.
 *
 * Silent throughout: the sentence that promised the panel has already been
 * spoken, and the pause is the point — it is the time the user has to look at
 * what opened before the walkthrough moves on.
 */
async function runReveal(step: TourStep, token: number): Promise<void> {
  const reveal = step.reveal;
  if (!reveal) return;
  // Nothing to show: say the line, skip the demonstration, move on. No pause.
  if (reveal.enabled && !reveal.enabled()) return;
  // A disabled button would swallow the click and leave the hold waiting on a
  // panel that is never coming.
  if (!isTargetActionable(reveal.open)) return;
  if (reveal.theme) return runRevealTheme(reveal, token);
  if (reveal.view) return runRevealView(reveal, token);
  if (!clickTarget(reveal.open)) return;
  await waitMs(REVEAL_OPEN_MS);
  if (token !== runToken || skipRequested || stopRequested) return;

  await waitMs(reveal.holdMs ?? REVEAL_HOLD_MS);
  // A run cut mid-hold leaves the panel where it is. Putting it away is Skip's
  // and Replay's job, not this one's: by now the token belongs to whatever
  // replaced this run, and closing from here would be closing over its work.
  if (token !== runToken || skipRequested || stopRequested) return;

  // Then the panel's other tabs, one at a time.
  //
  // What has already been seen is tracked rather than read off the screen each
  // time. The tab the panel opened on was the subject of the hold above, but it
  // stops being the selected one the moment the first switch happens — so a
  // fresh selected-test brought the loop straight back to it and showed it a
  // second time.
  const tabs = reveal.tabs ?? [];
  const seen = new Set(tabs.filter(isTargetSelected));
  for (const tab of tabs) {
    // A tab this design does not have is not in the DOM, and a disabled one
    // cannot be reached: both are passed over rather than held for nothing.
    if (seen.has(tab) || !isTargetActionable(tab)) continue;
    if (!clickTarget(tab)) continue;
    seen.add(tab);
    await waitMs(REVEAL_TAB_MS);
    if (token !== runToken || skipRequested || stopRequested) return;
    await waitMs(reveal.holdMs ?? REVEAL_HOLD_MS);
    if (token !== runToken || skipRequested || stopRequested) return;
  }

  if (reveal.close === "toggle") clickTarget(reveal.open);
  else closeTourPanels();
  await waitMs(REVEAL_CLOSE_MS);
}

/**
 * Show the application in the other theme, then put it back.
 *
 * Pressed for real, so the icon and its label change the way they do under a
 * finger. Put back by name rather than by a second press: the theme is written
 * to localStorage, so a run cut mid-hold would otherwise leave every later
 * session in the theme the walkthrough happened to stop on.
 */
async function runRevealTheme(reveal: TourReveal, token: number): Promise<void> {
  const startedDark = document.body.classList.contains("dark-mode");
  const live = () => token === runToken && !skipRequested && !stopRequested;

  // Pressed first, while its row is still on screen, so the icon and its label
  // flip under the highlight.
  if (!clickTarget(reveal.open)) return;
  await waitMs(REVEAL_OPEN_MS);

  // Then out of the way, so what is on show is the application rather than the
  // drawer sitting over it.
  if (reveal.overlay) {
    closeMobileOverlays();
    await waitMs(REVEAL_CLOSE_MS);
  }

  if (live()) await waitMs(reveal.holdMs ?? REVEAL_HOLD_MS);

  // By name, not by a second press: the row may well be off screen by now, and
  // the theme is written to localStorage, so a run cut mid-hold would otherwise
  // leave every later session in the theme this stopped on.
  document.dispatchEvent(
    new CustomEvent(VOICE_SET_THEME_EVENT, { detail: { value: startedDark ? "dark" : "light" } }),
  );
  await waitMs(REVEAL_CLOSE_MS);

  // Back as it was, so the next step finds the drawer it expects. Guarded on
  // the row being gone, because the control that opens the drawer closes it.
  if (reveal.overlay && live() && !isTargetVisible(reveal.open)) {
    clickTarget(reveal.overlay);
    await waitMs(320);
  }
}

/**
 * Show what a view control does, then put the view back as it was.
 *
 * The left bar and the mobile eye change nothing about the design, so naming
 * them teaches very little on its own — the point of each one is what it does
 * to the model. This walks the state through everything the control offers,
 * holding on each, and ends on the state the step began in.
 *
 * Silent throughout, like the panel reveals: the sentence has been spoken and
 * the pause is the time to look.
 */
async function runRevealView(reveal: TourReveal, token: number): Promise<void> {
  const key = reveal.view;
  if (!key) return;

  const started = (useViewerStore.getState() as any)[key];
  const cycle = VIEW_CYCLE[key];

  // A multi-state control is walked round from wherever it stands, so every
  // other state is shown once. An on/off one is simply flipped.
  let states: unknown[];
  if (cycle) {
    const at = cycle.indexOf(String(started));
    states = at < 0 ? [...cycle] : [...cycle.slice(at + 1), ...cycle.slice(0, at)];
  } else {
    states = [!started];
  }

  // State that rides along with this demonstration, remembered the same way so
  // it is put back with it.
  const alongside = reveal.alongside;
  const alongsideBefore: Record<string, unknown> = {};
  if (alongside) {
    const store = useViewerStore.getState() as any;
    for (const field of Object.keys(alongside)) alongsideBefore[field] = store[field];
  }

  const holdMs = reveal.holdMs ?? REVEAL_HOLD_MS;
  for (let i = 0; i < states.length; i++) {
    if (i === 0) {
      // The first change is a real press on the icon, so it behaves exactly as
      // it does under a finger — including the mobile eye menu closing itself,
      // which is what should happen and also clears the view for the hold.
      if (!clickTarget(reveal.open)) useViewerStore.setState({ [key]: states[i] } as any);
      // Switched on with that first change, not after it, so the two read as
      // one thing happening.
      if (alongside) useViewerStore.setState({ ...alongside } as any);
      await waitMs(REVEAL_OPEN_MS);
    } else {
      // Any state after that is set directly. The menu shut itself on that
      // first press, so pressing the icon again meant reopening the menu and
      // closing it a second time — an open/close flicker that showed nothing.
      useViewerStore.setState({ [key]: states[i] } as any);
    }
    await waitMs(holdMs);
    if (token !== runToken || skipRequested || stopRequested) break;
  }

  // Put back unconditionally, including on a cut run. A panel left open is
  // obvious and Skip and Replay clear it; a model left wireframed or showing
  // one foot just looks like the design, and nothing else would undo it.
  useViewerStore.setState({ [key]: started, ...alongsideBefore } as any);
}

/**
 * Whether the colour button has anything to colour.
 *
 * Mirrors the gate SideToolbar puts on its own button: a pressure scan on the
 * design, or a complete set of scanned images.
 */
function hasColouringData(): boolean {
  const design = useDesignStore.getState();
  if (design.insoleDesign?.pressureData) return true;
  const images = design.RondomImageData;
  return Array.isArray(images) && !images.some((img) => img == undefined);
}

/** How long to wait for TTS to start after SPEAK_EXACTLY on a tour line. */
const TOUR_SPEAK_START_MS = 8000;

/** Tutorial / break helpers still use a longer open-mic settle. */
const GREETING_START_MS = 6000;
const GREETING_MAX_MS = 20000;

/**
 * Wait until nothing is playing.
 *
 * Waiting for quiet alone is not enough right after a turn is triggered,
 * because the silence *before* the audio starts also reads as quiet. So this
 * first gives the audio a chance to appear, and only then waits for it to end.
 */
async function waitForSilence(startTimeoutMs: number, maxMs: number): Promise<void> {
  await waitUntilAgentAudible(startTimeoutMs);
  await waitUntilAgentQuiet(maxMs, QUIET_SETTLE_MS);
}

function clearLocalSpeechState(): void {
  resetAgentAudioTimeline();
  setAgentOutputVolume(0);
  setAgentSpeaking(false);
}

/**
 * Wait for a line to finish. Clock starts at the first audible frame.
 *
 * Must not end while audio is still playing — that cut the pressure line at
 * "350,000 clinical records…", twice: once on the hard deadline, and once by
 * mistaking the pause between two generated sentences for the end of the turn.
 * So the silence has to outlast that pause, and longer still before enough of
 * the estimate has played for the line to be plausibly over.
 * tightenHandoff only resets a stretched chunk timeline after real quiet.
 */
async function waitForLineToFinish(
  text: string,
  token: number,
  opts?: { tightenHandoff?: boolean }
): Promise<boolean> {
  const estimate = estimateSpeechMs(text);
  const sendAt = Date.now();
  // Generous: slower ElevenLabs TTS must not hit the deadline mid-sentence.
  const hardDeadline = sendAt + Math.round(estimate * 2.2) + 6000;
  let quietSince = 0;
  let heardAt = 0;
  // Most of the line must play before a breath is trusted as end-of-line.
  const holdRatio = opts?.tightenHandoff ? 0.8 : 0.88;

  while (Date.now() < hardDeadline) {
    if (token !== runToken || skipRequested || stopRequested) return heardAt > 0;

    if (isAgentAudible()) {
      if (!heardAt) heardAt = Date.now();
      quietSince = 0;
    } else if (heardAt) {
      if (!quietSince) quietSince = Date.now();
      // Past the min-hold the line may genuinely be over. Before it, the
      // estimate may simply have overshot the real speech, so hold out longer
      // rather than either cutting the line or waiting the estimate out.
      const minHoldAt = heardAt + Math.round(estimate * holdRatio);
      const settleMs = Date.now() >= minHoldAt ? LINE_END_QUIET_MS : LINE_STALL_QUIET_MS;
      if (Date.now() - quietSince >= settleMs) {
        // What the line really took, which is what keeps every later estimate
        // honest. It reads a shade long — the chunk timeline holds audible for
        // its own tail — and that is the safe direction: an estimate that errs
        // long never cuts a sentence short.
        noteSpokenLine(text, quietSince - heardAt);
        if (opts?.tightenHandoff) resetAgentAudioTimeline();
        return true;
      }
    }
    await waitMs(30);
  }

  // Deadline hit while still speaking: wait for real quiet, do not advance early.
  if (heardAt > 0) {
    const graceEnd = Date.now() + 10000;
    while (Date.now() < graceEnd) {
      if (token !== runToken || skipRequested || stopRequested) break;
      if (!isAgentAudible()) {
        await waitMs(QUIET_SETTLE_MS);
        break;
      }
      await waitMs(40);
    }
  }
  if (opts?.tightenHandoff) resetAgentAudioTimeline();
  return heardAt > 0;
}

/**
 * Speak one line and return once the assistant has finished saying it.
 *
 * Mic tracks are disabled while the line plays so speakerphone echo cannot
 * interrupt TTS. After send we wait for speaking (mode / onAudio / volume); if
 * detection fails on a phone we still hold for the estimated duration so the
 * tour does not abort into a silent open-mic after the welcome.
 */
async function speakStep(
  ctx: TourContext,
  produceLine: () => string | Promise<string>,
  token: number,
  onAudible?: () => void,
  opts?: { tightenHandoff?: boolean }
): Promise<boolean> {
  // Brief quiet only: the previous step already waited for the line to end.
  // Waiting up to 20s here (the old greeting window) is what made every gap
  // between narrations feel like a long silence on both phone and desktop.
  voiceLog("step:quietWait");
  // Skip the quiet poll when nothing is playing — the previous line already
  // waited for silence, and a 120ms wait here still added up across every step.
  if (isAgentSpeaking() || isAgentAudible()) {
    const quiet = await waitUntilAgentQuiet(BETWEEN_LINE_QUIET_MS, QUIET_SETTLE_MS);
    voiceLog("step:quietWaitDone", quiet);
    if (token !== runToken) return false;
    if (quiet === "timeout") clearLocalSpeechState();
  } else {
    voiceLog("step:quietWaitDone", "already-quiet");
  }

  // Only now: a line that drives the camera must not start doing so while the
  // previous sentence is still finishing.
  const text = await produceLine();
  if (token !== runToken || !text) return token === runToken;
  currentLine = text;

  // The conversation has a maximum duration on the agent, and a long tour can
  // reach it mid-run. Once the socket has gone every further send throws
  // "WebSocket is already in CLOSING or CLOSED state", so the tour stops here
  // rather than working its way through the remaining steps into a dead
  // connection.
  if (!ctx.isConnected()) return false;

  ctx.setMicMuted?.(true);
  try {
    clearLocalSpeechState();
    voiceLog("line:send", text.slice(0, 24));
    if (!ctx.speak(text)) return false;

    // Real audio only — the speaking-mode flag can flip before any sound and
    // made welcome "finish" before it started, jumping straight to pressure.
    let started = await waitUntilAgentSpeaking(TOUR_SPEAK_START_MS, {
      requireRisingEdge: true,
      requireAudio: true,
    });
    voiceLog("line:started", started);
    if (token !== runToken || !ctx.isConnected()) return false;

    if (started === "timeout" && isAgentAudible()) {
      started = "speaking";
    }

    if (started === "timeout") {
      await waitMs(80);
      if (token !== runToken || !ctx.isConnected()) return false;
      clearLocalSpeechState();
      if (!ctx.speak(text)) return false;
      started = await waitUntilAgentSpeaking(TOUR_SPEAK_START_MS, {
        requireRisingEdge: true,
        requireAudio: true,
      });
      voiceLog("line:resentStarted", started);
      if (token !== runToken || !ctx.isConnected()) return false;
      if (started === "timeout" && isAgentAudible()) {
        started = "speaking";
      }
    }

    onAudible?.();

    if (started === "timeout") {
      await waitMs(400);
      if (token !== runToken) return false;
      if (!isAgentAudible()) return false;
      const heard = await waitForLineToFinish(text, token, opts);
      return token === runToken && ctx.isConnected() && heard;
    }

    const heard = await waitForLineToFinish(text, token, opts);
    voiceLog("line:finished", heard ? 1 : 0);
    return token === runToken && ctx.isConnected() && heard;
  } finally {
    clearLocalSpeechState();
    // Exhibition stays deaf between lines. Opening the mic here is what let
    // booth noise interrupt the next SPEAK_EXACTLY (and break the sandal step).
    if (token === runToken && !ctx.deafMic) {
      ctx.setMicMuted?.(false);
    }
  }
}

async function runSteps(ctx: TourContext, steps: TourStep[], token: number): Promise<boolean> {
  lastSpokenIndex = -1;
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index];
    stepCursor = index;
    if (token !== runToken || skipRequested || stopRequested) return false;
    if (!ctx.isConnected()) return false;
    // Setup first, then the skip tests. The other way round, a step whose own
    // row had been hidden by the panel the previous step opened was dropped
    // before the setup that reopens the drawer could run — and with it went
    // every step after it in that section.
    await step.setup?.(ctx);
    if (token !== runToken || skipRequested || stopRequested) return false;
    if (step.skipWhenMissing && !hasTarget(step.skipWhenMissing)) continue;
    if (step.skipWhenBarHidden && !isGroupVisible(step.skipWhenBarHidden)) continue;

    // A leftover "repeat" from a prior step must not rewind this one — that
    // re-spoke additions-close and skipped the move command.
    repeatRequested = false;

    const speakThisStep = () =>
      speakStep(ctx, step.line, token, () => step.spotlight?.(ctx), {
        tightenHandoff: step.tightenHandoff,
      });
    let ok = await speakThisStep();
    // One retry only when the line never left the client — not after a
    // detection miss that already played audio (that re-read the same sentence).
    if (!ok && token === runToken && !skipRequested && !stopRequested && ctx.isConnected()) {
      await waitMs(80);
      clearLocalSpeechState();
      if (token === runToken && !skipRequested && !stopRequested) ok = await speakThisStep();
    }
    if (!ok || skipRequested || stopRequested) return false;
    lastSpokenIndex = index;

    await runReveal(step, token);
    if (token !== runToken || skipRequested || stopRequested) return false;

    await step.after?.(ctx);
    if (token !== runToken || skipRequested || stopRequested) return false;

    if (repeatRequested) {
      repeatRequested = false;
      index -= 1;
      continue;
    }

    if (step.awaitsAnswer) {
      const answer = await awaitAnswer(step.answerTimeoutMs, step.defaultAnswer);
      if (token !== runToken) return false;
      lastAnswer = answer;
      // What a given answer means depends on which question was asked, so the
      // branching lives with the caller that knows.
      if (answer === "skip") return false;
    }

    await waitMs(TOUR_STEP_GAP_MS);
  }
  return token === runToken && !skipRequested && !stopRequested;
}

/** Start the tour from the beginning. Safe to call while one is already running.
 *  Returns true when the script finished cleanly (so the host can mark it done). */
export async function startExhibitionTour(ctx: TourContext): Promise<boolean> {
  // Cut any in-flight run first (Replay mid-line / mid-break). Must not go
  // through stopExhibitionTour — that sets skipRequested and can dismiss.
  abortExhibitionTour();
  const token = ++runToken;
  running = true;
  skipRequested = false;
  stopRequested = false;
  answeringQuestion = false;
  repeatRequested = false;
  stepCursor = 0;
  lastAnswer = null;
  additionFocusRun = null;
  // Only a full run through the script counts as completed. Marking every exit
  // — including a race that never sent a line — left the stand with a finished
  // tour, an open microphone, and no way to auto-start again after the welcome.
  let finishedCleanly = false;

  // Deaf for the whole scripted run. Mic opens again only after a clean finish
  // (normal assistant). Skip closes the session entirely from the host.
  ctx.setMicMuted?.(true);
  // The stand is run in English, and the parameter step takes its words from
  // the four-language layer, so the language is pinned for the whole run.
  lockVoiceLang("en");
  // Autosave is deliberately left alone. The demonstration design is the
  // visitor's own copy, and the tour hands it to them with "change anything you
  // like" two steps before this function returns — so anything suppressed for
  // the duration of the run also swallowed the edits that invitation asked for.

  try {
    await resetViewerForTour();
    if (token !== runToken || skipRequested) return finishedCleanly;
    resetAgentAudioTimeline();
    setAgentOutputVolume(0);
    setAgentSpeaking(false);

    // One linear run. The visitor is shown the product on a prepared model;
    // there is nothing for them to choose before it starts.
    //
    // A break — a question, or "stop" — pauses it. The visitor is asked whether
    // to carry on and, if they do, the tour picks up where it was: at the next
    // line when the break came in the pause after one, since that line was heard
    // in full. Every break is handled the same way, however many there are.
    let from = 0;
    for (let breaks = 0; breaks <= MAX_TOUR_BREAKS; breaks += 1) {
      const remaining = DEMO_STEPS.slice(from);
      if (!remaining.length) {
        finishedCleanly = true;
        return finishedCleanly;
      }
      if (await runSteps(ctx, remaining, token)) {
        finishedCleanly = true;
        return finishedCleanly;
      }
      if (token !== runToken || skipRequested || !stopRequested) return finishedCleanly;

      const resumeAt = from + (lastSpokenIndex === stepCursor ? stepCursor + 1 : stepCursor);
      const breakResult = await handleTourBreak(ctx, token);
      if (breakResult === "abort") return finishedCleanly;
      if (breakResult === "end") {
        if (token === runToken && !skipRequested) {
          // They have said they are done, so this is a skip: the same event the
          // button fires, which closes the session and puts the controls away.
          skipExhibitionTour();
          ctx.onDismissed?.();
        }
        return finishedCleanly;
      }
      from = resumeAt;
    }
  } finally {
    if (token === runToken) {
      running = false;
      clearHighlights();
      highlightGroup(null);
      if (finishedCleanly) {
        // Back to the insole before normal mode — the demo ends on the sandal.
        await restoreInsoleAfterTour();
        if (token !== runToken) return finishedCleanly;
        // Last line done: hand the mic back like a normal (non-tour) session.
        ctx.setMicMuted?.(false);
        ctx.onFinished?.();
      } else if (!skipRequested) {
        // Abandoned mid-run without Skip — still release the mic.
        ctx.setMicMuted?.(false);
      }
      // Skip path: host ends the live session; leave tracks muted until then.
    }
  }
  return finishedCleanly;
}

/** A tour that is broken off more often than this is simply let go. */
const MAX_TOUR_BREAKS = 12;

/**
 * Deal with a break in the exhibition tour and say whether to carry on.
 *
 * After a question the assistant answers in its own words and ends by asking
 * whether to continue (the system prompt makes that part of its answer), so the
 * app only listens for the reply. After "stop" the app asks the question itself.
 * Another question asked while waiting is answered in turn, and the visitor is
 * asked again. The microphone is closed while the assistant speaks and open only
 * for the reply.
 */
async function handleTourBreak(ctx: TourContext, token: number): Promise<"resume" | "end" | "abort"> {
  for (let round = 0; round < MAX_TOUR_BREAKS; round += 1) {
    const afterQuestion = answeringQuestion;
    stopRequested = false;
    answeringQuestion = false;
    repeatRequested = false;
    if (afterQuestion) {
      await waitForSilence(GREETING_START_MS, GREETING_MAX_MS);
      if (token !== runToken || skipRequested) return "abort";
    }
    lastAnswer = null;
    const ok = await runSteps(
      ctx,
      [afterQuestion ? DEMO_QUESTION_CONTINUE : DEMO_STOP_CONFIRM],
      token
    );
    if (token !== runToken || skipRequested) return "abort";
    if (stopRequested) continue;
    // Speak/connection failure is not a visitor Skip — abandon the break
    // without dismissing the session (Replay used to trip this path).
    if (!ok) return "abort";
    return lastAnswer === "declineLesson" || lastAnswer === "skip" ? "end" : "resume";
  }
  return "resume";
}

/**
 * Run the interface walkthrough on its own.
 *
 * This is the half of the tour that names each part of the screen and bolds it,
 * without the exhibition welcome, the choice of halves, or the design-process
 * lessons. It backs the Tutorial button, which any user can press on any
 * design, so it must not assume a demonstration design is loaded: nothing here
 * changes what is visible in the viewer or touches the design.
 */
export async function startAppTutorial(ctx: TourContext): Promise<void> {
  const token = ++runToken;
  running = true;
  skipRequested = false;
  stopRequested = false;
  answeringQuestion = false;
  repeatRequested = false;
  stepCursor = 0;
  answerResolver = null;
  lastAnswer = null;

  // Deaf for the whole run, exactly as the demonstration is. The walkthrough
  // speaks line after line through SPEAK_EXACTLY, and an open microphone let
  // ambient noise register as a turn and interrupt the next line. The caller
  // also passes deafMic, which is what keeps it shut between lines.
  ctx.setMicMuted?.(true);
  // Start on a clear screen. Pressing Tutorial again part-way through a run
  // bumps the token rather than going through Skip, which leaves the previous
  // run's reveal holding a panel open — and the new run would then name its
  // first icons underneath it.
  closeTourPanels();
  closeMobileOverlays();
  // Unlike the exhibition demonstration, the tutorial follows whatever language
  // the user speaks — except where the session is pinned to one, which an
  // exhibition session is.
  if (!ctx.keepLanguage) unlockVoiceLang();

  try {
    // A suppressed greeting leaves nothing to wait for, so the run opens on a
    // short settle rather than listening out for audio that will never come.
    // Waiting the full window was most of the delay before the first word.
    if (ctx.expectsGreeting === false) {
      await waitMs(250);
    } else {
      await waitForSilence(GREETING_START_MS, GREETING_MAX_MS);
    }
    if (token !== runToken || skipRequested) return;

    const steps = isMobileLayout()
      ? [...TUTORIAL_OPENING, ...BAR_STEPS_MOBILE]
      : [...TUTORIAL_OPENING, ...BAR_STEPS, ...FOOTER_STEPS];

    if (!(await runSteps(ctx, steps, token))) {
      if (stopRequested) ctx.onDismissed?.();
      return;
    }

    if (token === runToken && !skipRequested && !stopRequested) {
      await runSteps(ctx, [TUTORIAL_CLOSING], token);
    }
  } finally {
    if (token === runToken) {
      running = false;
      // Whatever the last reveal opened, and the drawer or menu it was reached
      // from. A walkthrough that ends over its own open Settings panel has
      // handed the user a dialog they did not ask for.
      closeTourPanels();
      closeMobileOverlays();
      clearHighlights();
      highlightGroup(null);
      ctx.setMicMuted?.(false);
      ctx.onFinished?.();
    }
  }
}

/**
 * Stop the current tour so a Replay (or a fresh startExhibitionTour) can take
 * over. This is not Skip: the voice session stays up and the controls stay.
 */
export function abortExhibitionTour(): void {
  const pending = answerResolver;
  answerResolver = null;
  runToken += 1;
  running = false;
  skipRequested = false;
  stopRequested = false;
  answeringQuestion = false;
  repeatRequested = false;
  // Unstick any awaitsAnswer waiter. Token is already bumped, so the old run
  // exits without treating this as a visitor Skip.
  pending?.("next");
  closeTourPanels();
  closeMobileOverlays();
  clearHighlights();
  highlightGroup(null);
  stopVoiceOrbit();
  sandalOrbitRun = null;
  additionFocusRun = null;
}

/**
 * Stop the tour without touching the voice session. The assistant stays live so
 * the visitor keeps a working assistant after skipping, which is the whole
 * point of skipping rather than hanging up.
 *
 * Prefer abortExhibitionTour() when the next action is Replay / restart — this
 * path marks skipRequested and is what Skip uses.
 */
export function stopExhibitionTour(): void {
  const pending = answerResolver;
  answerResolver = null;
  runToken += 1;
  running = false;
  skipRequested = true;
  stopRequested = false;
  pending?.("skip");
  closeTourPanels();
  closeMobileOverlays();
  clearHighlights();
  highlightGroup(null);
  stopVoiceOrbit();
  sandalOrbitRun = null;
  additionFocusRun = null;
}

/** The line currently being spoken, for a "what did you say" style repeat. */
export function currentTourLine(): string {
  return currentLine;
}

/** Re-exported so the caller can label the session for the agent. */
export function tourLanguage(): VoiceLang {
  return currentVoiceLang();
}
